// Meridian by day — the city-by-day benchmark, in Bebop's brass. The law
// this scene is built to (city/space ANALYSIS §e):
//   - aerial perspective is a luminance LIFT: fog color IS the horizon sky
//     color, the far skyline lands within a couple of luma of the sky, and
//     nothing dark ever sits at the horizon;
//   - the skyline is an instanced Blender kit: three structure archetypes,
//     facade relief, and source-derived distant silhouettes;
//   - one landmark dome breaks the spire rhythm;
//   - traffic reads as shaped silhouettes at several depths (a vista with
//     fewer than two moving layers reads dead);
//   - one bright atmospheric core (the sun disc) stages the frame;
//   - sun authority: lit faces ~2.5x their shadow sides, chromatic sky —
//     the wave-9 critique, applied here from the start.
//
// Real kitbash depth is spent only where the camera walks: the Prom's two
// building rows and the port apron are true volumes on their collision
// footprints; the skyline beyond is dressing without collision — the same
// accepted gray-box compromise as ships clipping facades, logged in
// docs/design/the-port-city.md.

import {
  type Appearance,
  type AreaPlan,
  CITY_PAD,
  CITY_POSTER_WALL,
  cityGroundAt,
  cityHeightAt,
  PLANS,
  type PlayerPose,
  PROM,
  PROM_CLUTTER,
  PROM_NORTH_ROW,
  PROM_ROW_DEPTH,
  PROM_SOUTH_ROW,
  rand2,
  sanitizeAppearance,
  UNDERSTREET_GATE,
} from "@bebop/shared";
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { installProbe } from "./atmosphere";
import { contactPools } from "./attire";
import {
  bindAuthoredEnvironment,
  cloneAuthoredAsset,
  disposeAuthoredObject,
  instanceAuthoredAsset,
} from "./authored-assets";
import { buildWidth, type CrowdBatch, type CrowdLook, crowdBatch } from "./character";
import { type AreaScene, hexToCss, receivesShadows, signTexture, Tracker } from "./common";
import { cloth, dress, paint } from "./forge";
import { bindPortKitEnvironment, instancePortKit, type PortKitPlacement } from "./port-kit";
import { PosterWallLayer } from "./poster-wall";
import { catenaryGeometry, unitChamferBox } from "./solid";
import { TerrainLayer } from "./terrain";
import { groundMacro, pavingDetail } from "./textures";

// --- the day regime ---------------------------------------------------------

/**
 * Horizon/fog bone — the color every distant thing converges to.
 *
 * Wave 10 r2, the city critics' #1 gap ("day aerial perspective"). Three
 * facts had to line up and none of them did:
 *
 *  1. THREE MIXES FOG AFTER TONE MAPPING AND AFTER THE sRGB ENCODE
 *     (`tonemapping_fragment` → `colorspace_fragment` → `fog_fragment`),
 *     so a fully-fogged pixel lands on the fog hex LITERALLY. The sky
 *     dome, being ordinary geometry, was tone-mapped — so the same hex
 *     rendered ~34 luma darker in the sky than in the haze. That is the
 *     measured dB −38…−49 between "the colour towers converge to" and
 *     "the colour of the sky". The dome now renders untone-mapped (see
 *     buildDaySky) and the two are equal BY CONSTRUCTION.
 *  2. The hex itself was warm tan against a blue-grey sky. It is now the
 *     reference's own horizon anchor: a cool bone at Y≈216 (ref #d4dee3,
 *     Y220 — Bebop was delivering Y184).
 *  3. The knee sat at 200–1500 m, so window grids survived to the horizon
 *     at full contrast. See FOG_NEAR/FOG_FAR.
 */
const DAY_FOG = 0xd1d9dd;
/** Zenith: a real blue-grey, never achromatic (wave-9 lesson one). Lifted
 *  with the horizon — an untone-mapped dome renders these hexes as
 *  written, and the reference's day sky is an open, bright one. */
const DAY_ZENITH = 0x8fabc9;
/**
 * The haze knee. r1 ran 200 → 1500 m and the measured result was one
 * depth band instead of three: day-skyline's horizon ring came back
 * DARKER than its near tower (far/near 0.96×) with full-contrast black
 * window grids still legible at 2× zoom. The reference stack is near 124
 * / mid 165 / far 208 under a 220 sky, which is a knee that starts almost
 * at the lens and closes by ~700 m: at 55 m nothing is touched, 300 m is
 * ~40% gone, 500 m ~75%, and past 760 m a structure IS the sky.
 */
/**
 * Wave 10 r4. r3 delivered TWO depth steps where the law wants three: the
 * skyline verdict measured near 94–112 / mid 106–120 / far 157–187 and
 * called it — "the MID band does not lift… the haze knee is pushed all
 * the way to the back wall". Solve the knee for the reference stack
 * instead of guessing it. The reference is near 104 / mid 145 / far 217
 * under a 220 sky, so a mid structure at ~300 m must be 41% hazed and a
 * far one at ~700 m must be 100%: linear fog gives near = (300 − 0.41 ×
 * 700) / 0.59 = 22 m, far = 700 m. Rounded off the singularity to 28/700,
 * which also buys ~5 luma of the day-city exposure back for free (fog
 * colour is Y212 and it is now mixing over more of the frame).
 */
/**
 * WAVE 14. r4 solved the knee for a far band at 700 m and the frame does
 * not have one: measured on the delivered plate, the furthest LEGIBLE
 * tower band sits around 470 m, where 28/700 leaves it only 66% hazed and
 * 10.7% darker than the sky it stands against. Everything past ~700 m is
 * already indistinguishable from the sky and contributes no pixels the
 * judges can measure, so the knee was being solved against a band nobody
 * can see. Closing to 520 m puts that visible far band at ~95% and lands
 * it on the horizon bone, while 34 m of near clearance keeps the flanking
 * towers untouched (2% at 45 m) and the mid band inside its own step.
 */
/**
 * WAVE 15 — AND THE KNEE WAS SOLVED AS IF FOG WERE LINEAR. IT IS NOT.
 *
 * `THREE.Fog` is a SMOOTHSTEP between near and far (three's
 * ShaderChunk/fog_fragment), so its steepest region is its MIDPOINT, and the
 * midpoint is exactly where a distance ramp does the most damage to whatever
 * stands in it. 28/700 put that midpoint at 364 m — past everything legible.
 * 34/520 moved it to 277 m, into the dead centre of the band this vista's
 * mid-ground towers occupy, and the arithmetic is brutal: a tower at 250 m
 * kept 74.5% of its own contrast before and 58.3% after; at 350 m, 53.1%
 * before and 28.1% after. The window grid on the mid band is albedo and
 * nothing else (no emissive, no vertex term), so contrast lost to haze is
 * contrast gone, and the final judges read the mid band as flattened.
 *
 * The far band's lift does not need the knee to be that tight, because the
 * other half of the same commit — the sky dome's ramp and tessellation, see
 * `buildDaySky` — is what actually put the horizon bone where a hazed
 * structure converges, and it does that whatever the knee is. So the knee
 * goes back out to where it was BEFORE the wave that closed it, and the far
 * band keeps its lift from the sky instead of from the fog.
 */
const FOG_NEAR = 32;
const FOG_FAR = 700;
/**
 * Lifted-toe grade for the day city (wave 10 r2 REGRESSION FIX).
 *
 * The city was authored and critic-passed under LINEAR output on its own
 * branch; the merge put it under the house ACES grade and never re-
 * validated. Measured cost: p10 luma fell 40.2 → 22.1 on every day vista
 * and sub-Y32 mass went 4.8–9.3% → 13.6–15.5%, breaking the day exposure
 * band outright — while the extra contrast made the frames BETTER as
 * pictures. So the fix is not a revert. This is ACES with its toe lifted
 * to `CITY_BLACK_LIFT` in linear output: pure black lands at Y40 (measured
 * YMIN 31→40 with the lift at 0.021), the old Y22 pixels land ~Y48 (the
 * reference t020's own 43.6), and the sky moves by under two luma. Registered as CustomToneMapping so ONLY the day city
 * gets it — the frontier keeps house ACES, the Understreet keeps linear.
 */
const _CITY_BLACK_LIFT = 0.021;
/**
 * …AND THE LIFT IS SKY-COLOURED, WAVE 14.
 *
 * A lifted toe is standing in for the light that reaches a surface no key
 * can see, and in a city at noon that light is THE SKY. r2 lifted toward
 * neutral grey, which meant every deep shadow in three day vistas had
 * 0.021 of pure achromatic light added to it after grading — measured on
 * the day-street plate, the paving's shadow decile came back at (55.5,
 * 53.1, 49.2), a chroma ratio of 1.127 and effectively grey, against §e's
 * flat law that no shaded surface is ever achromatic. The floor of that
 * shadow was the operator, not the scene, so no amount of relighting could
 * reach it: cooling the hemisphere and the ambient by hand moved the
 * shadow's blue by 3%.
 *
 * These three numbers are the same LUMINANCE as 0.021 (0.2126r + 0.7152g +
 * 0.0722b = 0.0197) in a cool slate, so the exposure law the r4 gain was
 * measured against does not move and only the hue of the darkest eighth of
 * the frame does.
 */
const CITY_LIFT_RGB: readonly [number, number, number] = [0.0125, 0.0198, 0.0345];

// Register the operator. `CustomToneMapping` ships as an identity stub in
// three's tonemapping chunk; replacing the stub is the sanctioned hook,
// and the program cache keys on the tone-mapping define, so scenes that
// ask for ACES are untouched.
THREE.ShaderChunk.tonemapping_pars_fragment = THREE.ShaderChunk.tonemapping_pars_fragment.replace(
  "vec3 CustomToneMapping( vec3 color ) { return color; }",
  /* glsl */ `vec3 CustomToneMapping( vec3 color ) {
    vec3 graded = ACESFilmicToneMapping( color );
    return mix( vec3( ${CITY_LIFT_RGB.map((v) => v.toFixed(4)).join(", ")} ), vec3( 1.0 ), graded );
  }`,
);

const REBASE_DIST = 512;
const REBASE_GRID = 128;

/** Fixed art-directed sun. Low-ish and warm: raked light, long shadows. */
const SUN_DIR = new THREE.Vector3(-0.55, 0.62, 0.35).normalize();

// --- textures ---------------------------------------------------------------

/**
 * The facade atlas: bone masonry, dark window insets in a floor/column
 * grid, a brass trim line. Windows are texture speckle, never geometry —
 * the audit's cheapest confirmed trick. Day regime: insets stay dark, but
 * never below Y~34 (the day exposure band forbids content under Y32).
 *
 * `pitch` is the window-grid spacing in texels: the kit towers stretch
 * one texture over 26–88 m and want a dense grid; the Prom's 13–17 m
 * rows want a coarse one, or the two read as different scales of city.
 */
/**
 * Wave 10 r3. The r2 skyline verdict: "window 'speckle' is oversized
 * 25–30 px painted black rectangles, not the 1–3 px texture-scale speckle
 * the law specifies", and the day-street frame shows the same panes filling
 * half a facade. Two separate faults, both fixed here.
 *
 * SCALE: the sheet was 128×256 stretched over a whole tower face, so a
 * "floor" was four metres of texture and a pane was two. Doubling the sheet
 * and keeping the pitch in texels halves the projected pane and doubles the
 * row count, which is what puts the far towers into the 1–3 px band.
 *
 * VALUE: a pane at rgb(34,36,40) against a bone facade at 164 is a HOLE,
 * and thirty of them in a grid is pegboard. Real glass at midday is a
 * mirror: it carries the sky at the top of the pane, the building opposite
 * in the middle, and only goes dark in the reveal. So each pane is now a
 * three-stop vertical ramp with per-pane variance, a lit mullion between
 * panes and a spandrel band under each floor.
 */
/**
 * WHERE AN OBJECT MEETS THE GROUND (wave 12).
 *
 * The wave-11 judges measured this kit's one universal miss: "the contact/AO
 * darkening at prop bases measures identically before and after — 51.6 vs
 * 51.3 five pixels below contact". Meridian runs a shadow-casting sun, so
 * every prop here already throws a long shadow AWAY from itself; what none of
 * them had was the tight ambient wedge directly UNDER the footprint, which is
 * the term that actually welds an object to a plane. Without it a container
 * sits on the apron like a decal, and the eye reads it as pasted on.
 *
 * One instanced quad per set — the same wedge the avatar and the skiffs have
 * used since wave 11 (render/attire.ts), finally wired to the kit that has
 * hundreds of props in it. Daylight opacity: the sun is doing most of the
 * work here, so this is the contact term, not a drop shadow.
 */
function contactField(
  t: Tracker,
  parent: THREE.Object3D,
  spots: ReadonlyArray<{ x: number; y: number; z: number; r: number }>,
  opacity = 0.4,
  // The spot radii here are authored ~2.5x the prop's own base so the falloff
  // has somewhere to go, which puts the footprint at roughly 0.4 of the quad.
  // That is the core (see attire.ts): without it every one of these fields
  // spent its density under the crate instead of around it.
  core = 0.42,
): void {
  if (spots.length === 0) return;
  const mesh = contactPools(spots.length, opacity, core);
  t.mat(mesh.material as THREE.Material);
  const d = new THREE.Object3D();
  spots.forEach((s, i) => {
    d.position.set(s.x, s.y, s.z);
    d.rotation.set(-Math.PI / 2, 0, 0);
    d.scale.set(s.r, s.r, 1);
    d.updateMatrix();
    mesh.setMatrixAt(i, d.matrix);
  });
  parent.add(mesh);
}

// THE SKYLINE'S SHEET LEFT THIS FILE IN PHASE 3.
//
// It was written here in wave 13, when the skyline's hand-painted canvas was
// replaced by a real forged surface, and it was forged HERE because that is
// where the runtime forge lived. It is now `cityFacade` in
// tools/forge/src/surfaces.ts, baked offline at 2048² — 512 texels per metre
// instead of 128 — and reached like every other material in the game, by
// name, through `surface()`. The generator is unchanged apart from an ashlar
// course and a stone tooling octave that 512² had no room for, plus a
// weathering pass that puts the soot on the ledges rather than wherever an
// fbm happened to put it.
//
// `meanTarget` (0.213, the painted canvas's own measured mean) travels with
// the sheet in the manifest, so two thousand instanced towers multiply the
// same number they always did and the day-skyline anchors do not move.

/** Roof texture for the carpet blocks: mottled bone, tar seams, plant. */
function radialTexture(t: Tracker, inner: string, outer: string): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    const g = ctx.createRadialGradient(64, 64, 4, 64, 64, 64);
    g.addColorStop(0, inner);
    g.addColorStop(1, outer);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
  }
  return t.tex(new THREE.CanvasTexture(canvas));
}

// --- sky --------------------------------------------------------------------

/** Gradient day dome + the sun disc: the scene's one bright core. */
function buildDaySky(t: Tracker): THREE.Group {
  const sky = new THREE.Group();
  /**
   * WAVE 14 — THE AERIAL-PERSPECTIVE BUG WAS IN THE SKY, NOT IN THE FOG.
   *
   * The final judges measured the day-skyline's far tower band at 10.7%
   * BELOW the sky above it and called aerial perspective "running
   * backwards". Two lines here are why, and neither is the fog:
   *
   *  1. THE RAMP. `sqrt(up) * 0.9` is 26% of the way to zenith at five
   *     degrees of elevation and 46% at fifteen. Distant geometry lives
   *     between −5° and +12°, so the sky it is seen against was ALREADY a
   *     long way toward the zenith blue while a fully-hazed structure
   *     converges on the horizon bone by definition. Measured on the
   *     delivered plate: sky just over the skyline Y202, fog colour Y216,
   *     far towers Y180. A real sky has a broad pale band near the horizon
   *     — it is looking through the most air — and `smoothstep(0.005,
   *     0.62, up)` is that band: 4.7% at five degrees, 40% at fifteen, and
   *     the same shape the frontier's own sky shader has run since wave 9.
   *  2. THE TESSELLATION. 24 x 12 puts the first latitude ring at FIFTEEN
   *     DEGREES, so whatever the ramp says, the entire band that distant
   *     geometry occupies was one linear interpolation from horizon to the
   *     15° colour. 32 x 28 puts a ring every 6.4°.
   *
   * With the two of them the sky above the skyline lands on the fog colour
   * by construction, which is the whole of the "far band within ~5% of
   * sky" law — the knee (see FOG_FAR) only has to get the band there.
   */
  const geo = t.geo(new THREE.SphereGeometry(2300, 32, 28));
  const pos = geo.getAttribute("position");
  const colors = new Float32Array(pos.count * 3);
  const horizon = new THREE.Color(DAY_FOG);
  const zenith = new THREE.Color(DAY_ZENITH);
  const c = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const up = Math.max(0, pos.getY(i) / 2300);
    const t01 = Math.max(0, Math.min(1, (up - 0.005) / 0.615));
    c.copy(horizon).lerp(zenith, t01 * t01 * (3 - 2 * t01));
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const dome = new THREE.Mesh(
    geo,
    t.mat(
      new THREE.MeshBasicMaterial({
        vertexColors: true,
        side: THREE.BackSide,
        fog: false,
        depthWrite: false,
        // The whole aerial-perspective fix in one flag: three mixes fog
        // AFTER tone mapping, so a fully-hazed structure lands on the fog
        // hex literally. The dome has to land there too or the far band
        // can never equal the sky. (Wave 10 r2 — see DAY_FOG.)
        toneMapped: false,
      }),
    ),
  );
  dome.renderOrder = -2;
  sky.add(dome);

  // The sun: blown core + wide warm halo, in the staging quadrant.
  const halo = new THREE.Sprite(
    t.mat(
      new THREE.SpriteMaterial({
        map: radialTexture(t, "rgba(255,244,214,0.9)", "rgba(255,236,190,0)"),
        transparent: true,
        depthWrite: false,
        fog: false,
        // The sky is untone-mapped now; a tone-mapped disc would peak at
        // Y206 and read DARKER than the horizon it stages.
        toneMapped: false,
      }),
    ),
  );
  halo.position.copy(SUN_DIR).multiplyScalar(2150);
  halo.scale.set(1500, 1500, 1);
  halo.renderOrder = -1;
  sky.add(halo);
  const disc = new THREE.Sprite(
    t.mat(
      new THREE.SpriteMaterial({
        map: radialTexture(t, "#fffdf4", "rgba(255,250,230,0)"),
        transparent: true,
        depthWrite: false,
        fog: false,
        toneMapped: false,
      }),
    ),
  );
  disc.position.copy(SUN_DIR).multiplyScalar(2100);
  disc.scale.set(340, 340, 1);
  disc.renderOrder = -1;
  sky.add(disc);
  return sky;
}

// --- the instanced skyline ---------------------------------------------------

const KIT_SEED = 0x4d31a9 ^ 0x7c11;

interface KitPlacement {
  x: number;
  z: number;
  y: number;
  height: number;
  width: number;
  depth: number;
  archetype: number;
  tint: number;
}

/** Keep the Prom corridor, the pad, and the gate clear of kit towers. */
function kitBlocked(x: number, z: number): boolean {
  if (Math.abs(z) < 42 && x > -250 && x < 320) return true; // the Prom + port
  if (Math.hypot(x - CITY_PAD.x, z - CITY_PAD.z) < CITY_PAD.radius + 34) return true;
  if (Math.hypot(x + 210, z) < 40) return true; // the Understreet gate mouth
  return false;
}

/** Deterministic kit placements over the crown and terraces. */
function planKit(): KitPlacement[] {
  const placements: KitPlacement[] = [];
  const CELL = 56;
  const RANGE = 2400;
  for (let gz = -RANGE; gz < RANGE; gz += CELL) {
    for (let gx = -RANGE; gx < RANGE; gx += CELL) {
      const kx = Math.round(gx / CELL);
      const kz = Math.round(gz / CELL);
      const u = rand2(KIT_SEED, kx, kz);
      const x = gx + u * (CELL - 20);
      const z = gz + rand2(KIT_SEED + 1, kx, kz) * (CELL - 20);
      const r = Math.hypot(x, z);
      if (r < 120 || r > 2400) continue;
      if (kitBlocked(x, z)) continue;
      const ground = cityGroundAt(x, z);
      // The basin stays open fen — the skyline is crown and terraces.
      if (ground === "basin") continue;
      // Density falls off with distance; the far ring thins.
      const keep = ground === "crown" ? 0.95 : r < 1500 ? 0.86 : 0.64;
      if (rand2(KIT_SEED + 2, kx, kz) > keep) continue;
      const inner = ground === "crown";
      const height = inner
        ? 26 + rand2(KIT_SEED + 3, kx, kz) * 62
        : 14 + rand2(KIT_SEED + 3, kx, kz) * 40;
      const width = 14 + rand2(KIT_SEED + 4, kx, kz) * 16;
      const archetype = Math.floor(rand2(KIT_SEED + 5, kx, kz) * 3);
      const tint = rand2(KIT_SEED + 6, kx, kz);
      placements.push({
        x,
        z,
        y: cityHeightAt(x, z),
        height,
        width,
        depth: width * (0.7 + rand2(KIT_SEED + 7, kx, kz) * 0.6),
        archetype,
        tint,
      });
    }
  }
  return placements;
}

/** Blender architecture is grouped in spatial cells so whole districts can
 * leave the frustum. Far variants derive from tagged detail in the same source. */
interface CityPlacement {
  x: number;
  y: number;
  z: number;
  yaw?: number;
  width?: number;
  height?: number;
  depth?: number;
  scale?: number | [number, number, number];
}

function addCityAsset(
  t: Tracker,
  parent: THREE.Object3D,
  id: string,
  placements: CityPlacement[],
  cast = true,
): THREE.Group {
  const group = instanceAuthoredAsset(id, placements);
  group.traverse((object) => {
    if (object instanceof THREE.Mesh) object.castShadow = cast;
  });
  t.cleanup(() => disposeAuthoredObject(group));
  parent.add(group);
  return group;
}

function buildKit(
  t: Tracker,
  parent: THREE.Object3D,
  _kit: FacadeKit,
): (x: number, z: number) => void {
  const buckets = new Map<string, { id: string; placements: CityPlacement[] }>();
  const add = (id: string, placement: CityPlacement): void => {
    const key = `${id}:${Math.floor(placement.x / 512)}:${Math.floor(placement.z / 512)}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { id, placements: [] };
      buckets.set(key, bucket);
    }
    bucket.placements.push(placement);
  };
  for (const p of planKit()) {
    const kind = ["slab", "stepped", "spire"][p.archetype] ?? "slab";
    add(`city-${kind}`, {
      x: p.x,
      y: p.y - 2,
      z: p.z,
      scale: [p.width / 20, p.height / 60, p.depth / 20],
      yaw: p.archetype === 0 ? (Math.floor(p.tint * 4) * Math.PI) / 2 : 0,
    });
  }
  // Keep the previous roof-carpet plan, including its gaps and height tail.
  const cell = 23;
  const range = 1900;
  for (let gz = -range; gz < range; gz += cell) {
    for (let gx = -range; gx < range; gx += cell) {
      const kx = Math.round(gx / cell),
        kz = Math.round(gz / cell);
      const x = gx + rand2(KIT_SEED + 11, kx, kz) * (cell - 12);
      const z = gz + rand2(KIT_SEED + 12, kx, kz) * (cell - 12);
      const r = Math.hypot(x, z);
      if (r < 120 || r > range || kitBlocked(x, z) || cityGroundAt(x, z) === "basin") continue;
      if (rand2(KIT_SEED + 13, kx, kz) > 0.92) continue;
      const u = rand2(KIT_SEED + 15, kx, kz);
      add("city-roof-block", {
        x,
        y: cityHeightAt(x, z) - 2,
        z,
        scale: [
          (13 + rand2(KIT_SEED + 14, kx, kz) * 15) / 20,
          (7 + u ** 3 * 47) / 18,
          (13 + rand2(KIT_SEED + 16, kx, kz) * 15) / 20,
        ],
        yaw: (Math.floor(rand2(KIT_SEED + 17, kx, kz) * 4) * Math.PI) / 2,
      });
    }
  }
  const cells: {
    near: THREE.Group;
    far: THREE.Group;
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
  }[] = [];
  for (const bucket of buckets.values()) {
    const near = addCityAsset(t, parent, bucket.id, bucket.placements, false);
    const far = addCityAsset(t, parent, `${bucket.id}-far`, bucket.placements, false);
    near.visible = false;
    far.visible = false;
    cells.push({
      near,
      far,
      minX: Math.min(...bucket.placements.map((p) => p.x)) - 30,
      maxX: Math.max(...bucket.placements.map((p) => p.x)) + 30,
      minZ: Math.min(...bucket.placements.map((p) => p.z)) - 30,
      maxZ: Math.max(...bucket.placements.map((p) => p.z)) + 30,
    });
  }
  addCityAsset(t, parent, "city-dome", [{ x: 120, y: cityHeightAt(120, -260), z: -260 }], false);
  addCityAsset(
    t,
    parent,
    "city-campanile",
    [{ x: -330, y: cityHeightAt(-330, 285), z: 285 }],
    false,
  );
  let previousX = Infinity,
    previousZ = Infinity;
  return (x, z) => {
    if (Math.hypot(x - previousX, z - previousZ) < 12) return;
    previousX = x;
    previousZ = z;
    for (const cell of cells) {
      const dx = Math.max(cell.minX - x, 0, x - cell.maxX);
      const dz = Math.max(cell.minZ - z, 0, z - cell.maxZ);
      const distance = Math.hypot(dx, dz);
      // Detail follows the actual render camera, including flight/freecam.
      // Beyond the fog knee these solids contribute no distinguishable pixels.
      cell.near.visible = distance < 330;
      cell.far.visible = distance >= 330 && distance < FOG_FAR + 60;
    }
  };
}

// --- traffic ----------------------------------------------------------------

interface TrafficLane {
  radius: number;
  height: number;
  speed: number; // radians/s
  count: number;
  offset: number;
  /** Lane centre (wave 10 r3). Every lane used to be concentric on the
   *  world origin, so a vista parked 800 m off-centre saw one arc of each
   *  and the r2 critic counted 5 specks against a bar of ≥20. Off-centre
   *  lanes put traffic across every framing, not just the ones aimed at
   *  the middle of town. */
  cx?: number;
  cz?: number;
  /** Hull size class (wave 10 r4). Absent = the legacy radius ladder. */
  scale?: number;
}

/**
 * Wave 10 r2: the traffic law was measured at "1–2 airborne objects per
 * wide" against ">=20 specks across >=2 depth layers". Two causes, both
 * addressed here. (a) Only four rings existed and three of them sat past
 * 500 m — under the new 55→760 m knee anything out there IS the sky, so
 * the silhouettes that read are the ones inside 400 m, and there were
 * nine. (b) Every ring was concentric on the world origin, so a vista
 * parked 800 m off-centre saw one arc of each. Eight lanes now ladder
 * from 90 m to 1500 m with the mass in the legible band, at four heights
 * — depth layers you can count, which is what the law actually asks for.
 */
/**
 * WAVE 13 — THE FLOATING GREY SLABS.
 *
 * Two judges independently logged "floating grey slabs in the spaceport,
 * day-skyline and day-street skies — present in both sets, pre-existing, and
 * nobody has ever explained them". They are THIS: the traffic lanes. The
 * explanation is in three parts and all three are fixed here.
 *
 *  (a) THE HULL WAS A BOX. `BoxGeometry(9, 1.6, 3)` in a flat unlit colour
 *      is, at any distance, a grey slab — there is no wing, no fin, no
 *      nacelle, nothing that says which end is the front. `trafficHull()`
 *      below gives it an airframe.
 *  (b) IT WAS UNLIT. `MeshBasicMaterial` was chosen so hulls would read as
 *      silhouettes; what it actually does is remove the one cue that
 *      separates an object from a cut-out, which is that its top is lit and
 *      its underside is not. They are lit now, in a dark hull tone, and the
 *      fog still lifts them with distance.
 *  (c) THE LOW LANES CAME TOO CLOSE. The 130 m ring centred on (−150, 30)
 *      passes about thirty metres from the day-street lens at 44 m up: a
 *      nine-metre hull filling the top of the plate. Nothing at that range
 *      can look like traffic. Every lane now has a floor of 62 m and the
 *      near rings are pushed out, which costs no silhouettes — the count is
 *      unchanged — and moves them into the band where they read as air
 *      traffic instead of as debris.
 */
const LANES: TrafficLane[] = [
  // The packet lane: low and tight, so eye-level shots down the Prom get
  // a moving layer where the ring lanes only serve the wides.
  { radius: 190, height: 74, speed: 0.09, count: 7, offset: 1.1 },
  { radius: 165, height: 96, speed: -0.062, count: 10, offset: 4.4 },
  { radius: 260, height: 68, speed: 0.05, count: 12, offset: 2.2 },
  { radius: 355, height: 88, speed: -0.036, count: 12, offset: 5.6 },
  { radius: 520, height: 96, speed: 0.02, count: 14, offset: 0.4 },
  { radius: 690, height: 128, speed: -0.021, count: 14, offset: 2.9 },
  { radius: 900, height: 138, speed: -0.014, count: 20, offset: 1.7 },
  { radius: 1500, height: 175, speed: 0.009, count: 24, offset: 3.1 },
  // r3: six off-centre lanes. Three hang over the Prom's west end and the
  // Understreet gate (the day-street framing), three over the port and the
  // south-west terraces (the spaceport and skyline framings). Heights are
  // laddered so each vista reads traffic at ≥2 depths, which is the half of
  // the law that "20 specks in one ring" would still fail.
  { radius: 260, height: 82, speed: 0.075, count: 12, offset: 0.6, cx: -150, cz: 30 },
  { radius: 240, height: 106, speed: -0.045, count: 16, offset: 2.8, cx: -180, cz: -60 },
  { radius: 420, height: 132, speed: 0.028, count: 18, offset: 5.0, cx: -260, cz: 120 },
  { radius: 250, height: 90, speed: -0.058, count: 14, offset: 3.7, cx: 300, cz: -40 },
  { radius: 330, height: 112, speed: 0.033, count: 18, offset: 1.4, cx: 260, cz: 90 },
  { radius: 560, height: 126, speed: -0.019, count: 20, offset: 4.2, cx: -520, cz: 430 },
  // WAVE 10 r4. r3 still counted "~6–7 airborne traffic slabs vs the ≥20
  // bar, and all in ONE size class (no 2–6 px distant lane, no big
  // scale-beat ship)". Two causes. (a) Everything past ~450 m is >70%
  // haze, so half the ladder above is paying for silhouettes the fog
  // erases — the traffic budget has to live INSIDE the knee. (b) The size
  // ladder was derived from lane radius, so every lane a given vista could
  // actually see resolved to the same class. Lanes now declare their own
  // `scale`, and these six sit in the 70–330 m band where a hull still has
  // contrast, at three distinct classes: 0.4 packet, 1.0 lighter, 2.6
  // freighter.
  { radius: 230, height: 62, speed: 0.11, count: 14, offset: 2.4, scale: 0.42 },
  { radius: 210, height: 84, speed: -0.07, count: 16, offset: 0.9, scale: 0.55, cx: -120 },
  { radius: 300, height: 104, speed: 0.041, count: 14, offset: 3.3, scale: 1.5, cx: -40, cz: 60 },
  { radius: 260, height: 138, speed: -0.052, count: 10, offset: 5.2, scale: 2.6, cx: 240, cz: -30 },
  { radius: 300, height: 70, speed: 0.058, count: 12, offset: 1.8, scale: 0.45, cx: 300, cz: 40 },
  {
    radius: 385,
    height: 118,
    speed: -0.026,
    count: 12,
    offset: 4.9,
    scale: 2.2,
    cx: -430,
    cz: 350,
  },
];

/** Hull silhouettes in orbital lanes + one slow freighter beat. */
class TrafficLayer {
  readonly group = new THREE.Group();
  private meshes: { mesh: THREE.InstancedMesh; local: THREE.Matrix4 }[] = [];
  private freighter: THREE.Group;
  private dummy = new THREE.Object3D();
  private product = new THREE.Matrix4();
  private elapsed = 0;

  constructor(t: Tracker) {
    const total = LANES.reduce((n, lane) => n + lane.count, 0);
    const fleet = addCityAsset(
      t,
      this.group,
      "traffic-courier-lod",
      Array.from({ length: total }, () => ({ x: 0, y: 0, z: 0 })),
      false,
    );
    fleet.traverse((object) => {
      if (!(object instanceof THREE.InstancedMesh)) return;
      const local = new THREE.Matrix4();
      object.getMatrixAt(0, local);
      object.frustumCulled = false;
      this.meshes.push({ mesh: object, local });
    });
    this.freighter = cloneAuthoredAsset("traffic-freighter");
    t.cleanup(() => disposeAuthoredObject(this.freighter));
    this.group.add(this.freighter);
    this.sync(0);
  }

  sync(dt: number): void {
    this.elapsed += dt;
    let i = 0;
    for (const lane of LANES) {
      for (let k = 0; k < lane.count; k++) {
        const a = lane.offset + (k / lane.count) * Math.PI * 2 + this.elapsed * lane.speed;
        const x = (lane.cx ?? 0) + Math.cos(a) * lane.radius;
        const z = (lane.cz ?? 0) + Math.sin(a) * lane.radius;
        this.dummy.position.set(x, lane.height + Math.sin(a * 3) * 4, z);
        this.dummy.rotation.set(0, -a + (lane.speed < 0 ? Math.PI : 0), 0);
        // Size ladders with distance so every lane reads as roughly the
        // same class of vehicle seen from further away.
        const s = lane.scale ?? (lane.radius > 1200 ? 2 : lane.radius < 200 ? 0.55 : 1);
        this.dummy.scale.set(s, s, s);
        this.dummy.updateMatrix();
        for (const { mesh, local } of this.meshes)
          mesh.setMatrixAt(i, this.product.multiplyMatrices(this.dummy.matrix, local));
        i++;
      }
    }
    for (const { mesh } of this.meshes) mesh.instanceMatrix.needsUpdate = true;
    // r4: the scale beat comes inside the knee. At radius 1250 the 64 m
    // freighter sat 1.25 km out — 100% haze under a 700 m knee, i.e. it
    // was rendering the fog colour on the fog, which is why the verdict
    // read "no big scale-beat ship" in a scene that has always had one.
    // 430 m and 185 m up: ~58% hazed, so it still lifts with the mid band,
    // and it crosses the port half of the sky where two of three day
    // vistas are pointed.
    const fa = this.elapsed * 0.012 + 1.1;
    this.freighter.position.set(-90 + Math.cos(fa) * 430, 185, 40 + Math.sin(fa) * 430);
    this.freighter.rotation.y = -fa;
  }

  dispose(): void {
    this.meshes = [];
  }
}

// --- the facade kit -----------------------------------------------------------
//
// WAVE 13. Seven consecutive waves retargeted the material forge — the
// frontier settlement, the port containers, the apron, the club — and every
// one of them stopped short of the city's own buildings. The judges measured
// the result and it did not move a single byte between the last two builds:
// the day-street facade probe read micro 2.21 / macro 16.59 in both, which is
// the signature of ONE FLAT PLANE WITH PAINTED RECTANGLES ON IT. No mullions,
// no sills, no ledges, no dirt, no AO. Meanwhile the city references carry
// pipes, domes, setbacks and four or more depth layers on every facade.
//
// The fix is coverage, not a new system. Everything below is the forge and
// the contact field that already dress the frontier, aimed at the Meridian
// kit, plus the geometry that gives a facade DEPTH LAYERS:
//
//   1. the wall itself is a forged cast-panel surface, not a gradient sheet;
//   2. a window is a real recess — glass set back behind a sill that
//      protrudes 30 cm, jambs, a head, a centre mullion and a transom, so
//      the sun draws its own shadow lines across the elevation instead of
//      the texture painting them;
//   3. a string course at every floor and a pier between every bay, so the
//      elevation breaks vertically AND horizontally;
//   4. grime washes under every sill, because that is where a real building
//      is dirty and nowhere else;
//   5. plinth, cornice, parapet and a stepped setback, so the mass has a
//      bottom, a top and a silhouette;
//   6. downpipes, duct runs, extractor boxes and signage brackets — the
//      layer that reads as a working city rather than an architectural model.
//
// All of it goes through `Kitbash`, so the whole street is ~14 draw calls.

/** Storey height and window-bay pitch, metres. One city, one scale. */
const FLOOR_H = 3.45;
const BAY_W = 3.2;
/** Shop-front band height: floor one is a shop everywhere on the Prom. */
const SHOP_H = 3.4;

/** Accumulates (geometry, material) pairs and flushes them as instances. */
class Kitbash {
  private authored = new Map<string, CityPlacement[]>();
  constructor(private tracker: Tracker) {}
  asset(id: string, placement: CityPlacement): void {
    let placements = this.authored.get(id);
    if (!placements) {
      placements = [];
      this.authored.set(id, placements);
    }
    placements.push(placement);
  }
  private buckets = new Map<
    string,
    { geo: THREE.BufferGeometry; mat: THREE.Material; xf: THREE.Matrix4[] }
  >();
  private scratch = new THREE.Object3D();

  add(
    geo: THREE.BufferGeometry,
    mat: THREE.Material,
    scale: readonly [number, number, number],
    at: readonly [number, number, number],
    yaw = 0,
  ): void {
    const id = `${geo.uuid}:${mat.uuid}`;
    let bucket = this.buckets.get(id);
    if (!bucket) {
      bucket = { geo, mat, xf: [] };
      this.buckets.set(id, bucket);
    }
    this.scratch.position.set(at[0], at[1], at[2]);
    this.scratch.scale.set(scale[0], scale[1], scale[2]);
    this.scratch.rotation.set(0, yaw, 0);
    this.scratch.updateMatrix();
    bucket.xf.push(this.scratch.matrix.clone());
  }

  /** `cast` off for the skyline: 10 000 boxes a kilometre outside the
   *  shadow box are 10 000 boxes re-rendered into it for nothing. */
  flush(parent: THREE.Object3D, cast = true): void {
    for (const [id, placements] of this.authored)
      addCityAsset(this.tracker, parent, id, placements, cast);
    this.authored.clear();
    for (const bucket of this.buckets.values()) {
      if (bucket.xf.length === 0) continue;
      const mesh = new THREE.InstancedMesh(bucket.geo, bucket.mat, bucket.xf.length);
      bucket.xf.forEach((m, i) => {
        mesh.setMatrixAt(i, m);
      });
      mesh.castShadow = cast;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      parent.add(mesh);
    }
    this.buckets.clear();
  }
}

interface FacadeKit {
  box: THREE.BufferGeometry;
  cyl: THREE.BufferGeometry;
  wall: THREE.Material;
  trim: THREE.Material;
  reveal: THREE.Material;
  glass: THREE.Material;
  brass: THREE.Material;
  iron: THREE.Material;
  grime: THREE.Material;
}

/**
 * The wash a sill leaves on the wall under it.
 *
 * Every reference facade in the city audit is dirtiest in exactly two places
 * — under the sills and beside the downpipes — because that is where the
 * water goes. A uniform grime layer over a whole wall reads as a muddy
 * texture; grime with a CAUSE reads as weather.
 */
function grimeTexture(t: Tracker): THREE.CanvasTexture {
  const W = 64;
  const H = 64;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.clearRect(0, 0, W, H);
    for (let x = 0; x < W; x++) {
      const s = rand2(KIT_SEED + 91, x, 0);
      const s2 = rand2(KIT_SEED + 92, x, 1);
      // A streak starts at the sill and runs down until it dries out.
      const reach = 0.3 + s * 0.68;
      const strength = 0.22 + s2 * 0.5;
      for (let y = 0; y < H; y++) {
        const v = y / H;
        if (v > reach) continue;
        const fall = (1 - v / reach) ** 1.6;
        const a = Math.min(0.74, strength * fall * (0.7 + rand2(KIT_SEED + 93, x, y) * 0.95));
        ctx.fillStyle = `rgba(38,34,28,${a.toFixed(3)})`;
        ctx.fillRect(x, y, 1, 1);
      }
    }
    // The dark line right under the lip, where the drip never leaves.
    ctx.fillStyle = "rgba(30,27,22,0.5)";
    ctx.fillRect(0, 0, W, 2);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return t.tex(texture);
}

/** The forged materials every Meridian facade is built from. */
function facadeKit(t: Tracker): FacadeKit {
  const std = (rough: number, metal = 0): THREE.MeshStandardMaterial =>
    new THREE.MeshStandardMaterial({ roughness: rough, metalness: metal });
  const kit: FacadeKit = {
    // Chamfered, for the same reason the skyline masses are (see buildKit):
    // this one geometry is every sill, cornice, string course, pier, jamb
    // and mullion on the Prom, and a sill whose lip catches a bright line
    // is the difference between a modelled facade and a printed one.
    box: t.geo(unitChamferBox(0.02).clone()),
    cyl: t.geo(new THREE.CylinderGeometry(0.5, 0.5, 1, 8)),
    // Cast panel on a running bond: a port capital builds in precast, and
    // the joint grid is the macro octave a flat gradient never had.
    wall: paint(std(0.94), "paving", 0xb5ab95, {
      orm: true,
      meters: 4,
      normalScale: 0.95,
      sharpen: 6,
    }),
    // Sills, cornices, string courses: dressed stone, one value up so a
    // ledge separates from the wall it stands on even in shade.
    trim: paint(std(0.9), "stone", 0xd8ceb7, { orm: true, meters: 2, normalScale: 0.85 }),
    // The reveal: what you see INSIDE a window opening, and the spandrel
    // panels between floors. Dark, so a recess reads as a recess.
    reveal: paint(std(0.88), "plate", 0x605c51, { orm: true, meters: 2, normalScale: 0.9 }),
    glass: dress(std(0.3, 0.45), "glass", 0x3f4750, { orm: true, meters: 2 }),
    brass: dress(std(0.45, 0.62), "brass", 0x8a6f3c, { orm: true, meters: 1 }),
    iron: dress(std(0.62, 0.4), "iron", 0x4a4740, { orm: true, meters: 1 }),
    grime: new THREE.MeshBasicMaterial({
      map: grimeTexture(t),
      transparent: true,
      depthWrite: false,
      opacity: 0.85,
    }),
  };
  for (const key of ["wall", "trim", "reveal", "glass", "brass", "iron", "grime"] as const) {
    t.mat(kit[key]);
  }
  return kit;
}

/** Where a facade's local frame points, given the outward normal's yaw. */
interface Face {
  /** Centre of the elevation, world. */
  cx: number;
  cz: number;
  /** Outward normal, as a Y rotation: 0 = +z, π/2 = +x. */
  yaw: number;
  /** Elevation width, metres. */
  width: number;
  /** Wall top, metres above the building's base. */
  top: number;
  /** Ground level. */
  base: number;
  /** Skip the grime and the street furniture on faces nobody walks past. */
  street: boolean;
}

/**
 * ONE ELEVATION, IN FOUR DEPTH LAYERS.
 *
 * Layer 0 is the wall plane. Layer 1 is the spandrel and the recessed glass.
 * Layer 2 is the frame — jambs, mullion, transom, head. Layer 3 is what
 * stands proud of all of it: sills, string courses and piers. A raking sun
 * on four layers is what the reference frames are full of and what a painted
 * quad can never produce, however good the paint.
 */
function elevation(kit: Kitbash, _m: FacadeKit, f: Face): void {
  const floors = Math.max(1, Math.floor((f.top - SHOP_H - 1.2) / FLOOR_H));
  const bays = Math.max(2, Math.round(f.width / BAY_W));
  const pitch = f.width / bays;
  for (let floor = 0; floor < floors; floor++) {
    for (let bay = 0; bay < bays; bay++) {
      const u = -f.width / 2 + (bay + 0.5) * pitch;
      kit.asset("city-window-bay", {
        x: f.cx + u * Math.cos(f.yaw),
        y: f.base + SHOP_H + floor * FLOOR_H,
        z: f.cz - u * Math.sin(f.yaw),
        yaw: f.yaw,
        scale: [pitch / 3.2, 1, 1],
      });
    }
  }
}

/**
 * The mass itself: plinth, cornice, parapet, a stepped setback, roof plant,
 * and the service layer — downpipes with collars, a duct run over the shop
 * band, extractors, and signage brackets swinging over the pavement.
 */
function facadeMass(
  kit: Kitbash,
  m: FacadeKit,
  at: { x: number; z: number; w: number; d: number; h: number; base: number },
  street: { z: number; out: number },
): void {
  const { x, z, w, d, h, base } = at;
  const put = (
    mat: THREE.Material,
    sx: number,
    sy: number,
    sz: number,
    px: number,
    py: number,
    pz: number,
    geo: THREE.BufferGeometry = m.box,
  ): void => {
    kit.add(geo, mat, [sx, sy, sz], [px, base + py, pz]);
  };
  // A building has a bottom: a plinth in dressed stone, with a chamfer band.
  put(m.trim, w + 0.7, 0.9, d + 0.7, x, 0.45, z);
  put(m.reveal, w + 0.5, 0.18, d + 0.5, x, 0.99, z);
  // …and a top. Cornice, brass drip and a parapet standing over it — the
  // three elements that stop a mass reading as an extruded rectangle.
  for (const side of [-1, 1]) {
    kit.asset("city-cornice", {
      x,
      y: base + h - 0.5,
      z: z + (side * d) / 2,
      yaw: side < 0 ? Math.PI : 0,
      width: w + 1,
      height: 0.5,
      depth: 0.7,
    });
    kit.asset("city-cornice", {
      x: x + (side * w) / 2,
      y: base + h - 0.5,
      z,
      yaw: (side * Math.PI) / 2,
      width: d + 1,
      height: 0.5,
      depth: 0.7,
    });
  }
  put(m.brass, w + 0.78, 0.14, d + 0.78, x, h - 0.58, z);
  put(m.wall, w + 0.34, 1.3, d + 0.34, x, h + 0.65, z);
  put(m.trim, w + 0.5, 0.16, d + 0.5, x, h + 1.38, z);
  // The setback: one storey stepped in, with its own cornice. A skyline of
  // flat tops is the single loudest greybox tell there is.
  const sw = Math.max(3, w - 3.4);
  const sd = Math.max(3, d - 5);
  put(m.wall, sw, 3.4, sd, x - w * 0.08, h + 1.7, z);
  put(m.trim, sw + 0.6, 0.36, sd + 0.6, x - w * 0.08, h + 3.5, z);
  kit.asset("city-roof-plant", { x: x + w * 0.25, y: base + h + 1.3, z: z + d * 0.15 });

  // The service layer. Downpipes run the full height at the corners of the
  // street elevation, collared every four metres, and stain the wall.
  const faceZ = street.z;
  for (const s of [-1, 1]) {
    const px = x + s * (w / 2 - 0.45);
    put(m.iron, 0.22, h - 0.4, 0.22, px, (h - 0.4) / 2, faceZ + street.out * 0.16, m.cyl);
    for (let y = 2.2; y < h - 1; y += 4.2) {
      put(m.iron, 0.34, 0.18, 0.34, px, y, faceZ + street.out * 0.2, m.cyl);
    }
    put(m.grime, 0.55, h * 0.55, 0.02, px, h * 0.3, faceZ + street.out * 0.05);
  }
  // Duct run over the shop band, with two extractors and a riser.
  put(m.iron, w * 0.66, 0.44, 0.44, x, SHOP_H + 0.5, faceZ + street.out * 0.42, m.cyl);
  for (const s of [-1, 1]) {
    put(m.reveal, 0.8, 0.8, 0.7, x + s * w * 0.24, SHOP_H + 0.5, faceZ + street.out * 0.55);
  }
  put(m.iron, 0.3, 2.6, 0.3, x + w * 0.34, SHOP_H + 1.9, faceZ + street.out * 0.34, m.cyl);
  // Signage brackets: an arm out over the pavement and a board hanging off
  // it. Silhouette in the near field, which is where the eye rests.
  const arm = 1.5;
  put(m.iron, 0.09, 0.09, arm, x - w * 0.26, 4.6, faceZ + street.out * (arm / 2 + 0.1));
  put(m.iron, 0.09, 0.9, 0.09, x - w * 0.26, 4.15, faceZ + street.out * (arm - 0.1));
  put(m.brass, 1.05, 0.75, 0.07, x - w * 0.26, 3.55, faceZ + street.out * (arm - 0.1));
  put(m.iron, 0.07, 0.07, 0.9, x + w * 0.34, 6.3, faceZ + street.out * 0.55);
  put(m.reveal, 0.5, 1.6, 0.06, x + w * 0.34, 5.6, faceZ + street.out * 0.95);
}

// --- the Prom and the port ---------------------------------------------------

/** True volumes on the Prom's collision footprints, doors, signs, gate. */
function dressProm(t: Tracker, parent: THREE.Object3D, plan: AreaPlan, kit: FacadeKit): void {
  const prom = new THREE.Group();
  // WAVE 13: the row buildings' windows are GEOMETRY now (see `elevation`),
  // so the wall is a wall — a forged cast-panel surface with joints, grain
  // and a real normal, not a gradient sheet with rectangles painted on it.
  const bash = new Kitbash(t);
  const rowMat = kit.wall;

  // Paved avenue: a long slab, slightly proud of the crown, with a
  // flagstone joint texture — an untextured foreground plane was most of
  // the street shot's bottom half.
  const pavingTex = (() => {
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      // Wave 10 r3 — UNLIGHT THE FLOOR. The r2 city verdict's headline:
      // "the pavement is 45% of frame at Y143 with sd 25.3 and near-zero
      // detail… Bebop's day near-field mean is 137.9 under a Y201 sky
      // while the reference's near-field is 108.8 under a BRIGHTER Y219
      // sky. The reference's foreground is darker and busier; Bebop's is
      // brighter and emptier." A city street is asphalt and wet-stained
      // flag, not desert playa: the albedo drops ~28% so the brightest 5%
      // of the frame is sky, and the sheet gains four times the incident
      // detail so the darkness is not just a flatter flat.
      ctx.fillStyle = "#68635a";
      ctx.fillRect(0, 0, 64, 64);
      for (let i = 0; i < 56; i++) {
        const v = rand2(KIT_SEED + 61, i, 1);
        ctx.fillStyle = v > 0.5 ? "rgba(255,255,255,0.05)" : "rgba(22,19,15,0.10)";
        ctx.fillRect(
          rand2(KIT_SEED + 62, i, 2) * 62,
          rand2(KIT_SEED + 63, i, 3) * 62,
          2 + v * 11,
          2 + v * 9,
        );
      }
      // Grit: the fine speckle a swept-stone street always carries.
      for (let i = 0; i < 340; i++) {
        const v = rand2(KIT_SEED + 64, i, 5);
        ctx.fillStyle = v > 0.62 ? "rgba(190,182,166,0.30)" : "rgba(18,15,12,0.32)";
        ctx.fillRect(rand2(KIT_SEED + 65, i, 6) * 64, rand2(KIT_SEED + 66, i, 7) * 64, 1, 1);
      }
      ctx.strokeStyle = "rgba(28,24,18,0.5)";
      ctx.lineWidth = 1.4;
      ctx.strokeRect(0.5, 0.5, 63, 63);
      ctx.beginPath();
      ctx.moveTo(32, 0);
      ctx.lineTo(32, 64);
      ctx.stroke();
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(58, 5);
    return t.tex(texture);
  })();
  const paving = new THREE.Mesh(
    t.geo(new THREE.BoxGeometry(PROM.maxX - PROM.minX + 60, 0.12, PROM.halfDepth * 2 + 18)),
    t.mat(new THREE.MeshStandardMaterial({ map: pavingTex, roughness: 0.96 })),
  );
  paving.position.set((PROM.minX + PROM.maxX) / 2, -0.02, 0);
  prom.add(paving);

  const rows: Array<{ x: number; width: number; north: boolean; venue?: string }> = [
    ...PROM_NORTH_ROW.map((b) => ({ ...b, north: true })),
    ...PROM_SOUTH_ROW.map((b) => ({ ...b, north: false })),
  ];
  // Shared storefront materials: the ground floor of every row building
  // is a shop, or the Prom reads as a canyon of offices over bare paving.
  const awningMats = [0x7a4436, 0x5c6146, 0x74624a].map((c) =>
    t.mat(cloth("canvas", c, { meters: 2 })),
  );
  rows.forEach((b, i) => {
    const height = 13 + ((i * 29) % 5) * 2.2;
    const zCenter = b.north
      ? -PROM.halfDepth - PROM_ROW_DEPTH / 2
      : PROM.halfDepth + PROM_ROW_DEPTH / 2;
    const building = new THREE.Mesh(
      t.geo(new THREE.BoxGeometry(b.width, height, PROM_ROW_DEPTH)),
      rowMat,
    );
    building.position.set(b.x, height / 2, zCenter);
    building.castShadow = true;
    building.receiveShadow = true;
    prom.add(building);

    // The storefront: dark ground-floor band, brass rail over it, an
    // awning in one of three canvas colors, and a door inset.
    const face = b.north ? -PROM.halfDepth : PROM.halfDepth;
    const outward = b.north ? 1 : -1;

    // WAVE 13 — the depth layers. The street elevation gets the full
    // treatment; the two end elevations get it too, because the day-street
    // vista looks ALONG the row and every gap shows one. The back wall
    // faces the kit and is never framed, so it stays a wall.
    facadeMass(
      bash,
      kit,
      { x: b.x, z: zCenter, w: b.width, d: PROM_ROW_DEPTH, h: height, base: 0 },
      { z: face, out: outward },
    );
    elevation(bash, kit, {
      cx: b.x,
      cz: face,
      yaw: b.north ? 0 : Math.PI,
      width: b.width,
      top: height,
      base: 0,
      street: true,
    });
    for (const s of [-1, 1]) {
      elevation(bash, kit, {
        cx: b.x + s * (b.width / 2),
        cz: zCenter,
        yaw: s * (Math.PI / 2),
        width: PROM_ROW_DEPTH,
        top: height,
        base: 0,
        street: false,
      });
    }
    const shopCount = Math.max(2, Math.round(b.width / 3.2));
    for (let k = 0; k < shopCount; k++) {
      const u = -b.width / 2 + ((k + 0.5) * b.width) / shopCount;
      if (b.venue && Math.abs(u) < 2.2) continue;
      bash.asset("city-shop-bay", {
        x: b.x + u,
        y: 0,
        z: face,
        yaw: b.north ? 0 : Math.PI,
        scale: [b.width / shopCount / 3.2, 1, 1],
      });
    }
    if (!b.venue) {
      const awning = new THREE.Mesh(
        t.geo(new THREE.BoxGeometry(b.width * 0.62, 0.08, 1.5)),
        awningMats[i % awningMats.length],
      );
      awning.position.set(b.x - b.width * 0.1, 3.1, face + outward * 0.85);
      awning.rotation.x = outward * 0.22;
      prom.add(awning);
      const door = new THREE.Mesh(
        t.geo(new THREE.BoxGeometry(1.7, 2.5, 0.3)),
        t.mat(new THREE.MeshStandardMaterial({ color: 0x2a2c30, roughness: 0.5 })),
      );
      door.position.set(b.x + b.width * 0.28, 1.25, face + outward * 0.14);
      prom.add(door);
    }

    if (b.venue) {
      const target = PLANS[b.venue];
      const accent = target?.accent ?? plan.accent;
      const door = new THREE.Mesh(
        t.geo(new THREE.BoxGeometry(2.8, 3, 0.3)),
        t.mat(new THREE.MeshStandardMaterial({ color: 0x0d0d12, roughness: 0.4 })),
      );
      door.position.set(b.x, 1.5, face + (b.north ? 0.1 : -0.1));
      prom.add(door);
      const frameMat = t.mat(
        new THREE.MeshStandardMaterial({ color: accent, emissive: accent, emissiveIntensity: 0.7 }),
      );
      const top = new THREE.Mesh(t.geo(new THREE.BoxGeometry(3.3, 0.18, 0.34)), frameMat);
      top.position.set(b.x, 3.1, face + (b.north ? 0.12 : -0.12));
      prom.add(top);
      const sign = new THREE.Mesh(
        t.geo(new THREE.PlaneGeometry(8.4, 1.6)),
        t.mat(
          new THREE.MeshBasicMaterial({
            map: signTexture(t, target?.label ?? b.venue, hexToCss(accent)),
            transparent: true,
            side: THREE.DoubleSide,
          }),
        ),
      );
      sign.position.set(b.x, 5.2, face + (b.north ? 0.2 : -0.2));
      if (!b.north) sign.rotation.y = Math.PI;
      prom.add(sign);
    }
  });

  // Market clutter: stalls with canvas awnings on the plan's clutter boxes.
  const promContacts: Array<{ x: number; y: number; z: number; r: number }> = [];
  addCityAsset(
    t,
    prom,
    "city-kiosk",
    PROM_CLUTTER.map((box) => {
      const x = (box.minX + box.maxX) / 2,
        z = (box.minZ + box.maxZ) / 2;
      promContacts.push({
        x,
        y: 0.09,
        z,
        r: Math.max(box.maxX - box.minX, box.maxZ - box.minZ) * 2.1,
      });
      return {
        x,
        y: 0,
        z,
        width: box.maxX - box.minX + 0.8,
        height: 2.4,
        depth: box.maxZ - box.minZ + 0.8,
      };
    }),
  );

  // Street lamps down the Prom: brass poles, milk-glass globes (unlit by
  // day — Meridian's surface room is only ever daylit). At the walk
  // margin, matching the semaphore line; sub-collision scale, logged in
  // the port-city doc with the other gray-box compromises.
  addCityAsset(
    t,
    prom,
    "city-lamp",
    [
      [-180, 8.6],
      [-120, -8.6],
      [-60, 8.6],
      [20, -8.6],
      [100, 8.6],
      [180, -8.6],
    ].map(([x = 0, z = 0]) => {
      promContacts.push({ x, y: 0.09, z, r: 1.9 });
      return { x, y: 0, z, height: 6.1, width: 0.8, depth: 0.8 };
    }),
  );

  // The Understreet gate: a brass-lipped ramp mouth sinking under the
  // west end — the way DOWN into the night. Its glow is the cool accent.
  const gate = new THREE.Group();
  const zone = UNDERSTREET_GATE.zone;
  const cx = (zone.minX + zone.maxX) / 2;
  const mouth = new THREE.Mesh(
    t.geo(new THREE.BoxGeometry(10, 6.4, 9)),
    t.mat(
      paint(new THREE.MeshStandardMaterial({ roughness: 0.7 }), "plate", 0x191d22, {
        orm: true,
        meters: 4,
      }),
    ),
  );
  mouth.position.set(cx - 3.4, 3.2, 0);
  gate.add(mouth);
  addCityAsset(t, gate, "city-gate", [
    { x: zone.maxX + 0.6, y: 0, z: 0, yaw: Math.PI / 2, width: 10.4, height: 7.6, depth: 1.2 },
  ]);
  const gateGlowMat = t.mat(
    new THREE.MeshStandardMaterial({ color: 0x2fb3c0, emissive: 0x2fb3c0, emissiveIntensity: 1.2 }),
  );
  for (const gz of [-4.6, 4.6]) {
    const jamb = new THREE.Mesh(t.geo(new THREE.BoxGeometry(0.5, 6.8, 0.5)), gateGlowMat);
    jamb.position.set(zone.maxX + 0.6, 3.4, gz);
    gate.add(jamb);
  }
  const gateSign = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(9, 1.5)),
    t.mat(
      new THREE.MeshBasicMaterial({
        map: signTexture(t, "the understreet", "#59c8d4"),
        transparent: true,
        side: THREE.DoubleSide,
      }),
    ),
  );
  gateSign.position.set(zone.maxX + 1.3, 8, 0);
  gateSign.rotation.y = Math.PI / 2;
  gate.add(gateSign);
  prom.add(gate);

  // Where the row meets the pavement. A plinth without a contact wedge is
  // still a decal; this is the term the wave-11 judges measured missing on
  // every vertical in the city, walked along both building lines.
  for (const b of rows) {
    const face = b.north ? -PROM.halfDepth : PROM.halfDepth;
    for (let u = -b.width / 2 + 1; u <= b.width / 2 - 1; u += 2.4) {
      promContacts.push({ x: b.x + u, y: 0.07, z: face + (b.north ? 1.1 : -1.1), r: 3.2 });
    }
  }
  bash.flush(prom);
  contactField(t, prom, promContacts, 0.5);
  dressStreetLevel(t, prom);

  parent.add(prom);
}

/**
 * One volume standing on the Prom's pavement: where it is, which archetype
 * it is, and how much floor it takes.
 */
interface PromProp {
  x: number;
  z: number;
  /** Footprint half-extents in plan, metres. `rx` 0 = flush, walk over it. */
  rx: number;
  rz: number;
  /** The archetype roll, and the variant roll behind it. */
  roll: number;
  roll2: number;
  /** −1 / +1 for kerb pieces, 0 for the roadway. */
  side: number;
  kerb: boolean;
  yaw: number;
}

/**
 * THE PLAN OF THE STREET — one list, built once, read twice.
 *
 * `dressStreetLevel` builds these volumes and `CrowdLayer` walks around
 * them, and until this list existed the second half of that sentence was
 * not true: the crowd's lane was a constant, so ninety-six pedestrians
 * walked through whatever the deterministic hash had put on the pavement.
 * Two of them were photographed doing it — one half-buried in the black
 * kiosk at (1340, 700) on the day-street plate, one straddling a shopfront
 * plinth with its front leg inside the stone and daylight under its rear
 * boot. A crowd that walks through the furniture is not dressing; it is the
 * defect the furniture was added to hide.
 *
 * Footprints are ELLIPSES rather than discs because most of these pieces
 * are long and thin — a bollard pair is 1.6 m of x and 16 cm of z, and a
 * disc round it would make the whole crowd swerve two metres for nothing.
 */
function promProps(): PromProp[] {
  const props: PromProp[] = [];
  // Kerb furniture, walked along the full run of the Prom at ~5 m spacing
  // with the archetype chosen off the deterministic hash — so a camera
  // parked anywhere on the street has a dense, varied first 20 m.
  for (let x = PROM.minX - 16; x < PROM.maxX + 16; x += 4.6) {
    for (const side of [-1, 1]) {
      const roll = rand2(KIT_SEED + 71, Math.round(x), side);
      // The remaining ~16% stays empty: uniform density is its own tell.
      if (roll >= 0.84) continue;
      const roll2 = rand2(KIT_SEED + 72, Math.round(x), side);
      // The footprint of each archetype, in the order `dressStreetLevel`
      // builds them: bollard pair, vent stack, crate stack, cable spool,
      // kiosk, sign frame, planter.
      const foot: readonly [number, number] =
        roll < 0.14
          ? [1.6, 0.15]
          : roll < 0.28
            ? [0.95, 0.95]
            : roll < 0.42
              ? // Crate stack: a 1.0 x 0.9 box turned on a hashed yaw is
                // 0.68 on the diagonal, and the upper crate is offset 0.3.
                [1.05, 0.85]
              : roll < 0.54
                ? [0.72, 0.72]
                : roll < 0.66
                  ? [1.2, 0.95]
                  : roll < 0.76
                    ? [0.32, 0.32]
                    : [0.75, 0.75];
      props.push({
        x,
        z: side * (PROM.halfDepth - 1.1 - roll2 * 0.9),
        rx: foot[0],
        rz: foot[1],
        roll,
        roll2,
        side,
        kerb: true,
        yaw: side > 0 ? 0 : Math.PI,
      });
    }
  }
  // In the roadway itself. The kerb furniture fixed the mid-band but left the
  // bottom 300 rows of an eye-level frame as bare pavement (measured sd 15.4
  // against a reference t017 of 57.0), because everything was pushed to the
  // edges. Real streets have things standing in them: vent grates, a parked
  // handcart, a delivery stack, a bollard someone left. Sparse enough to walk
  // through, dense enough that no framing gets an empty foreground.
  for (let x = PROM.minX - 10; x < PROM.maxX + 10; x += 8.6) {
    const roll = rand2(KIT_SEED + 76, Math.round(x), 9);
    if (roll > 0.62) continue;
    // A vent grate is flush with the paving: it is walked over, not round.
    const foot: readonly [number, number] =
      roll < 0.2 ? [0, 0] : roll < 0.36 ? [1.95, 0.62] : roll < 0.5 ? [0.75, 0.7] : [1.0, 0.35];
    props.push({
      x,
      z: (rand2(KIT_SEED + 77, Math.round(x), 11) - 0.5) * (PROM.halfDepth * 1.5),
      rx: foot[0],
      rz: foot[1],
      roll,
      roll2: 0,
      side: 0,
      kerb: false,
      yaw: rand2(KIT_SEED + 78, Math.round(x), 13) * Math.PI,
    });
  }
  return props;
}

/**
 * THE FIRST TWENTY METRES (wave 10 r3). The r2 city verdict's single
 * biggest-gap note, verbatim: "the near field is empty and lit backwards…
 * in every vista the bottom 35–50% of frame is a bare, evenly-lit plane
 * with no props, no contact shadows and no greeble, while both references
 * put their densest, highest-contrast detail in exactly that band.
 * Measured local contrast (sd of the lower band): day-street 25.3,
 * spaceport 15.1 versus reference t017 at 57.0." Unlighting the floor was
 * half of the prescription; this is the other half — kiosks, vents, cable
 * runs, sign frames and kerb furniture, instanced, dense at the kerbs and
 * thinning into the roadway, every one of them a shadow caster so the
 * band gains contact AO as well as silhouettes.
 */
function dressStreetLevel(t: Tracker, parent: THREE.Object3D): void {
  const box = t.geo(new THREE.BoxGeometry(1, 1, 1));
  const cyl = t.geo(new THREE.CylinderGeometry(0.5, 0.5, 1, 10));
  // WAVE 12: every one of these was a flat colour, in the band the r3
  // verdict called "the first twenty metres" — the part of the frame a
  // viewer's eye is actually resting on. Same props, same positions, same
  // graded values; the forge supplies the grain, the joints and the grime.
  const mats = {
    dark: paint(new THREE.MeshStandardMaterial({ roughness: 0.9 }), "plate", 0x3c3830, {
      orm: true,
      meters: 2,
      normalScale: 0.8,
    }),
    brass: dress(
      new THREE.MeshStandardMaterial({ roughness: 0.42, metalness: 0.65 }),
      "brass",
      0x8a6f3c,
      { orm: true, meters: 1 },
    ),
    canvasA: cloth("canvas", 0x7a4436, { meters: 2 }),
    canvasB: cloth("canvas", 0x4e5a44, { meters: 2 }),
    crate: dress(new THREE.MeshStandardMaterial({ roughness: 1 }), "wood", 0x6b5a42, {
      meters: 2,
    }),
    steel: paint(
      new THREE.MeshStandardMaterial({ roughness: 0.55, metalness: 0.5 }),
      "plate",
      0x6e6d68,
      { orm: true, meters: 2, normalScale: 0.9 },
    ),
  } as const;
  for (const m of Object.values(mats)) t.mat(m);
  /** Where each cluster meets the paving. */
  const contacts: Array<{ x: number; y: number; z: number; r: number }> = [];
  type MatKey = keyof typeof mats;
  // Everything below is accumulated per (geometry, material) and flushed as
  // InstancedMeshes: ~450 props at ~12 draw calls. Dressing the near field
  // is not allowed to cost the 60 fps budget.
  const buckets = new Map<
    string,
    { geo: THREE.BufferGeometry; key: MatKey; xf: THREE.Matrix4[] }
  >();
  const scratch = new THREE.Object3D();
  const add = (
    geo: THREE.BufferGeometry,
    key: MatKey,
    sx: number,
    sy: number,
    sz: number,
    x: number,
    y: number,
    z: number,
    yaw = 0,
  ): void => {
    const id = `${geo.uuid}:${key}`;
    let bucket = buckets.get(id);
    if (!bucket) {
      bucket = { geo, key, xf: [] };
      buckets.set(id, bucket);
    }
    scratch.position.set(x, y, z);
    scratch.scale.set(sx, sy, sz);
    scratch.rotation.set(0, yaw, 0);
    scratch.updateMatrix();
    bucket.xf.push(scratch.matrix.clone());
  };
  const flush = (): void => {
    for (const bucket of buckets.values()) {
      const mesh = new THREE.InstancedMesh(bucket.geo, mats[bucket.key], bucket.xf.length);
      bucket.xf.forEach((m, i) => {
        mesh.setMatrixAt(i, m);
      });
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      parent.add(mesh);
    }
  };

  // Kerbs: the street's two long horizontals. Nothing reads "street" to
  // the eye faster, and they anchor everything scattered along them.
  for (const side of [-1, 1]) {
    add(
      box,
      "dark",
      PROM.maxX - PROM.minX + 40,
      0.22,
      0.6,
      (PROM.minX + PROM.maxX) / 2,
      0.11,
      side * (PROM.halfDepth - 0.3),
    );
  }

  const authored = new Map<string, CityPlacement[]>();
  const place = (
    id: string,
    x: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    yaw = 0,
    y = 0,
  ): void => {
    let batch = authored.get(id);
    if (!batch) {
      batch = [];
      authored.set(id, batch);
    }
    batch.push({ x, y, z, width, height, depth, yaw });
  };
  for (const prop of promProps()) {
    const { x, z, roll: r, roll2: r2, yaw } = prop;
    if (prop.kerb) {
      if (r < 0.14) {
        place("city-bollard", x, z, 0.24, 0.95, 0.24);
        place("city-bollard", x + 1.5, z, 0.24, 0.95, 0.24);
      } else if (r < 0.28) {
        place("city-roof-plant", x, z, 1.3, 1.65, 1.3, yaw + r2);
      } else if (r < 0.42) {
        place("frontier-crate", x, z, 1, 0.8, 0.9, yaw + r2 * 0.6);
        place("frontier-crate", x + 0.3, z + 0.1, 0.8, 0.7, 0.8, yaw - r2, 0.8);
      } else if (r < 0.54) {
        place("city-spool", x, z, 1.4, 1.4, 0.7, yaw);
      } else if (r < 0.66) {
        place("city-kiosk", x, z, 2.4, 2.3, 1.9, yaw);
      } else if (r < 0.76) {
        place("frontier-notice-board", x, z, 1.6, 3.4, 0.3, yaw + Math.PI / 2);
      } else {
        place("city-planter", x, z, 1.42, 3, 1.42);
      }
    } else if (r < 0.2) {
      place("city-vent-grate", x, z, 2.4, 0.09, 1.6, yaw);
    } else if (r < 0.36) {
      place("frontier-handcart", x, z, 3.1, 1.4, 1.25, yaw + Math.PI / 2);
    } else if (r < 0.5) {
      place("city-pallet", x, z, 1.35, 1.12, 1.25, yaw);
    } else {
      place("city-bollard", x, z, 0.22, 0.9, 0.22);
      place("city-bollard", x + 0.9, z + 0.4, 0.14, 0.5, 0.14);
    }
    contacts.push({ x, y: 0.08, z, r: prop.kerb ? 3.6 : 3.8 });
  }
  for (const [id, placements] of authored) addCityAsset(t, parent, id, placements);

  // Cable runs across the canyon: the reference's single most characteristic
  // near-field element, and free depth cueing.
  //
  // WAVE 14 — "catenary-ish" was a 0.09 x 0.09 x 24 m BOX, i.e. a perfectly
  // straight horizontal line drawn across the top of every day-street frame.
  // A dead-level cable is the most conspicuous "this is a model" signal in
  // the set, because it is the one element a viewer knows from life must
  // sag. These are real catenaries now, solved for a 6% sag, merged into one
  // geometry and one draw call. See @bebop/forge/solid.
  const promCables: THREE.BufferGeometry[] = [];
  for (let x = PROM.minX; x < PROM.maxX; x += 26) {
    const r = rand2(KIT_SEED + 73, Math.round(x), 3);
    const h = 6.5 + r * 5;
    const reach = PROM.halfDepth + 4;
    promCables.push(
      catenaryGeometry({ x, y: h, z: -reach }, { x, y: h + (r - 0.5) * 0.5, z: reach }, 1.5, 0.06),
    );
    // Two lamps hanging off it, on drops of their own.
    for (const side of [-1, 1]) {
      add(cyl, "brass", 0.3, 0.34, 0.3, x, h - 0.72, side * 4.2);
      add(cyl, "dark", 0.03, 0.5, 0.03, x, h - 0.34, side * 4.2);
    }
  }
  flush();
  const promCableGeo = mergeGeometries(promCables, false);
  for (const g of promCables) g.dispose();
  if (promCableGeo) {
    const run = new THREE.Mesh(
      t.geo(promCableGeo),
      t.mat(new THREE.MeshStandardMaterial({ color: 0x24211d, roughness: 0.7, metalness: 0.35 })),
    );
    run.castShadow = true;
    parent.add(run);
  }
  contactField(t, parent, contacts, 0.5);
}

/**
 * The apron's working ground (wave 10 r3). The spaceport vista stands east
 * of the pad looking west, and the r2 verdict measured its bottom half as
 * "a bare landing pad at Y149 with sd 15.1 — the flattest, emptiest
 * foreground in the set". The pad itself is only 30 m across; everything
 * the camera actually stands on is apron, and an apron is the busiest
 * ground in a port: container stacks, blast walls, bowsers, cable trunking,
 * marker lights and painted haul lanes. Instanced in rings around the pad
 * so it dresses the near field from every approach, not just this one.
 */
function dressApron(t: Tracker, parent: THREE.Object3D): void {
  const box = t.geo(new THREE.BoxGeometry(1, 1, 1));
  /**
   * WAVE 12 — the apron takes the material forge.
   *
   * The judges' 2x crop was exact: "the nearest spaceport container is still
   * a flat-shaded teal box: no panel lines, no corrugation, no wear, no
   * logo, one value per face". That was literally true — every material in
   * this function was a bare colour, and the whole forge (render/forge.ts)
   * had only ever been wired to the frontier settlement.
   *
   * `paint()` rather than `dress()`, and that distinction is the design of
   * the fix: a container yard's colour is PER BOX (teal, oxide, galvanise),
   * so the two new sheets are neutral and hand the paint back. The mean
   * luminance of each material is unchanged to within the tint arithmetic,
   * which is how a surface pass lands without moving a graded frame.
   */
  const mats = {
    deck: dress(new THREE.MeshStandardMaterial({ roughness: 0.94 }), "dirt", 0x4c4a42, {
      orm: true,
      meters: 4,
    }),
    steel: paint(
      new THREE.MeshStandardMaterial({ roughness: 0.6, metalness: 0.45 }),
      "plate",
      0x8b8578,
      { orm: true, meters: 4, normalScale: 0.9 },
    ),
    rust: paint(new THREE.MeshStandardMaterial({ roughness: 0.93 }), "container", 0x7d543a, {
      orm: true,
      meters: 4,
      normalScale: 1.1,
    }),
    teal: paint(new THREE.MeshStandardMaterial({ roughness: 0.88 }), "container", 0x3f6a68, {
      orm: true,
      meters: 4,
      normalScale: 1.1,
    }),
    lamp: t.mat(
      new THREE.MeshStandardMaterial({
        color: 0xd8b04a,
        emissive: 0xd8b04a,
        emissiveIntensity: 0.8,
      }),
    ),
  } as const;
  for (const m of [mats.deck, mats.steel, mats.rust, mats.teal]) t.mat(m);
  type MatKey = keyof typeof mats;
  const buckets = new Map<
    string,
    { geo: THREE.BufferGeometry; key: MatKey; xf: THREE.Matrix4[] }
  >();
  const scratch = new THREE.Object3D();
  const add = (
    geo: THREE.BufferGeometry,
    key: MatKey,
    sx: number,
    sy: number,
    sz: number,
    x: number,
    y: number,
    z: number,
    yaw = 0,
  ): void => {
    const id = `${geo.uuid}:${key}`;
    let bucket = buckets.get(id);
    if (!bucket) {
      bucket = { geo, key, xf: [] };
      buckets.set(id, bucket);
    }
    scratch.position.set(x, y, z);
    scratch.scale.set(sx, sy, sz);
    scratch.rotation.set(0, yaw, 0);
    scratch.updateMatrix();
    bucket.xf.push(scratch.matrix.clone());
  };

  // Every freight placement now uses the source-derived port kit LOD.
  const freightPlacements: PortKitPlacement[] = [];
  const container = (
    key: MatKey,
    x: number,
    y: number,
    z: number,
    yaw: number,
    len: number,
    height: number,
    depth: number,
  ): void => {
    void key;
    freightPlacements.push({ x, y: y - height / 2, z, yaw, width: len, height, depth });
  };

  /** Where each prop meets the deck — collected, then laid in one call. */
  const contacts: Array<{ x: number; y: number; z: number; r: number }> = [];

  // Two working rings around the pad, plus a scattered outer band. Local
  // coordinates: the caller has already translated to CITY_PAD.
  const rings: ReadonlyArray<readonly [number, number]> = [
    [CITY_PAD.radius + 9, 26],
    [CITY_PAD.radius + 26, 34],
    [CITY_PAD.radius + 52, 40],
  ];
  const apronAssets = new Map<string, CityPlacement[]>();
  const prop = (id: string, x: number, z: number, yaw: number): void => {
    let placements = apronAssets.get(id);
    if (!placements) {
      placements = [];
      apronAssets.set(id, placements);
    }
    placements.push({ x, y: 0, z, yaw });
  };
  rings.forEach(([radius, count], ring) => {
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + ring * 0.31;
      const jitter = rand2(KIT_SEED + 81, i, ring);
      const r = radius + (jitter - 0.5) * 14;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      const yaw = a + (rand2(KIT_SEED + 82, i, ring) - 0.5) * 0.8;
      const pick = rand2(KIT_SEED + 83, i, ring);
      if (pick < 0.26) {
        // Container stack: the apron's one repeating readable volume.
        const key: MatKey = pick < 0.09 ? "rust" : pick < 0.18 ? "teal" : "steel";
        container(key, x, 1.3, z, yaw, 6.1, 2.6, 2.44);
        if (jitter > 0.45) {
          container(jitter > 0.72 ? "rust" : "teal", x, 3.9, z, yaw, 6.1, 2.6, 2.44);
        }
        contacts.push({ x, y: 0.03, z, r: 8.4 });
      } else if (pick < 0.42) {
        prop("city-blast-wall", x, z, yaw + Math.PI / 2);
        contacts.push({ x, y: 0.03, z, r: 9 });
      } else if (pick < 0.55) {
        prop("city-bowser", x, z, yaw);
        contacts.push({ x, y: 0.03, z, r: 7.6 });
      } else if (pick < 0.68) {
        prop("city-cable-trunk", x, z, yaw);
        contacts.push({ x, y: 0.03, z, r: 6.2 });
      } else if (pick < 0.8) {
        prop("city-marker-mast", x, z, yaw);
        contacts.push({ x, y: 0.03, z, r: 2.4 });
      } else if (pick < 0.9) {
        prop("city-pallet", x, z, yaw);
        contacts.push({ x, y: 0.03, z, r: 4.6 });
      }
    }
  });

  // Haul lanes: painted guidance stripes running off the pad, which is the
  // one cue that says "vehicles cross this ground" on an empty apron.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.22;
    for (let s = 0; s < 9; s++) {
      const r = CITY_PAD.radius + 6 + s * 7.5;
      add(box, "steel", 3.4, 0.06, 0.42, Math.cos(a) * r, 0.22, Math.sin(a) * r, a);
    }
  }

  for (const bucket of buckets.values()) {
    const mesh = new THREE.InstancedMesh(bucket.geo, mats[bucket.key], bucket.xf.length);
    bucket.xf.forEach((m, i) => {
      mesh.setMatrixAt(i, m);
    });
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    parent.add(mesh);
  }
  for (const [id, placements] of apronAssets) addCityAsset(t, parent, id, placements);
  const freight = instancePortKit("freight-container-lod", freightPlacements);
  freight.traverse((object) => {
    if (object instanceof THREE.InstancedMesh) {
      t.cleanup(() => object.dispose());
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) t.mat(material);
    }
  });
  parent.add(freight);
  contactField(t, parent, contacts, 0.52);
}

/** The port: apron, gantries, pylons, the customs house, moored dressing. */
function dressPort(t: Tracker, parent: THREE.Object3D): void {
  const port = new THREE.Group();
  port.position.set(CITY_PAD.x, 0, CITY_PAD.z);

  // r3: the apron drops with the Prom paving. It was ~50% of the spaceport
  // frame at Y149 with sd 15.1 — "the flattest, emptiest foreground in the
  // set" — and a landing pad is scorched deck plate, not a beach.
  const apron = new THREE.Mesh(
    t.geo(new THREE.CylinderGeometry(CITY_PAD.radius, CITY_PAD.radius + 1, 0.4, 40)),
    // The pad is bolted deck plate, not a coloured disc: joints, bolt rows
    // and the oil that collects on a working pad, at 8 m panels.
    t.mat(
      paint(
        new THREE.MeshStandardMaterial({ roughness: 0.92, metalness: 0.06 }),
        "plate",
        0x565349,
        {
          orm: true,
          meters: 8,
          normalScale: 0.7,
        },
      ),
    ),
  );
  apron.position.y = 0.2;
  port.add(apron);
  const ring = new THREE.Mesh(
    t.geo(new THREE.TorusGeometry(CITY_PAD.radius - 1.6, 0.22, 8, 56)),
    t.mat(
      new THREE.MeshStandardMaterial({
        color: 0xd8b04a,
        emissive: 0xd8b04a,
        emissiveIntensity: 0.55,
      }),
    ),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.44;
  port.add(ring);

  addCityAsset(t, port, "city-crane", [
    { x: -44, y: 0, z: -26, yaw: 0.4 },
    { x: 12, y: 0, z: -44, yaw: -0.3 },
    { x: 8, y: 0, z: 42, yaw: 2 },
  ]);
  addCityAsset(t, port, "city-customs", [
    { x: 205 - CITY_PAD.x, y: 0, z: -25, width: 18, height: 9.8, depth: 14 },
  ]);
  const customsSign = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(10, 1.4)),
    t.mat(
      new THREE.MeshBasicMaterial({
        map: signTexture(t, "meridian landing", "#d8b04a"),
        transparent: true,
        side: THREE.DoubleSide,
      }),
    ),
  );
  customsSign.position.set(205 - CITY_PAD.x, 10.4, -25);
  port.add(customsSign);

  // Cargo: crate stacks on pallets between the cranes. The pallet slab
  // grounds each stack — an unshadowed dark box on bright paving reads
  // as floating (round-one lesson); a wider, darker plinth under it
  // reads as contact.
  const cargoContacts: Array<{ x: number; y: number; z: number; r: number }> = [];
  const cargo: CityPlacement[] = [];
  for (let i = 0; i < 10; i++) {
    const a = rand2(KIT_SEED + 40, i, 1) * Math.PI * 2;
    const r = CITY_PAD.radius + 6 + rand2(KIT_SEED + 41, i, 2) * 16;
    const size = 1.2 + rand2(KIT_SEED + 42, i, 3) * 1.6;
    const x = Math.cos(a) * r,
      z = Math.sin(a) * r;
    cargo.push({
      x,
      y: 0,
      z,
      width: size * 1.9,
      height: size + 0.18,
      depth: size * 1.7,
      yaw: a * 3,
    });
    cargoContacts.push({ x, y: 0.03, z, r: size * 3.4 });
  }
  addCityAsset(t, port, "city-pallet", cargo);
  cargoContacts.push({ x: 205 - CITY_PAD.x, y: 0.04, z: -25, r: 30 });
  contactField(t, port, cargoContacts, 0.5);

  dressApron(t, port);

  parent.add(port);
}

// --- alive-world staples -----------------------------------------------------

/** Steam vents on the core roofs; semaphore masts that flick their arms. */
/** Pedestrians on the Prom (wave 10 r4). */
const CROWD_N = 96;

/**
 * THE PROM'S PEOPLE ARE PEOPLE (wave 13).
 *
 * What stood here was an assembly of loose primitives — a capsule, a cross
 * plate for shoulders, a sphere head and a cylinder brim — and the judges
 * were exact about it: "a flat disc hat hovering above a detached sphere
 * head… announces itself as a tech demo within a second". The wave that
 * wrote it was reasoning about 200 m, where a stance is everything and a
 * face is noise; the day-street vista puts two of these ten metres from the
 * lens, where a detached head is the only thing in the frame.
 *
 * There has been a real figure in render/humanoid.ts for two waves — jointed
 * limbs with knees and elbows, hands, a garment cut that reads in
 * silhouette. `crowdFigure()` bakes it into one geometry per rigid node so
 * the crowd can instance it: four looks × seven nodes = 28 draw calls for
 * ninety-six walking people, against the two thousand a scene graph each
 * would have cost. The walk is the humanoid's own cycle, evaluated here and
 * written into instance matrices.
 *
 * Variation is per-instance where it is free (height as a uniform scale,
 * value jitter through `instanceColor`) and per-LOOK where it is not
 * (garment cut, palette, build, wear) — four bakes is the whole budget, and
 * four silhouettes in a crowd of ninety-six is more than the eye counts.
 */
const CROWD_LOOKS: ReadonlyArray<Partial<Appearance>> = [
  // A duster over dark trousers: the street's default coat.
  { build: 1, height: 0.5, skinTone: 2, hair: 1, hairColor: 1, outfit: 1, outfitColor: 5, wear: 1 },
  // Deckhand's vest, bare forearms — the port half of the crowd.
  {
    build: 2,
    height: 0.72,
    skinTone: 5,
    hair: 0,
    hairColor: 0,
    outfit: 2,
    outfitColor: 7,
    wear: 2,
  },
  // A machinist under a flat cap, on their way down to the Understreet.
  {
    build: 0,
    height: 0.28,
    skinTone: 8,
    hair: 3,
    hairColor: 3,
    outfit: 3,
    outfitColor: 2,
    wear: 2,
    accessory: 1,
  },
  // Freighter coat, road-worn: someone who came in on a hull this morning.
  { build: 3, height: 0.6, skinTone: 6, hair: 6, hairColor: 1, outfit: 4, outfitColor: 5, wear: 2 },
];

/** One walker: which bake wears it, and where on the Prom it is. */
interface Walker {
  look: number;
  slot: number;
  lane: number;
  speed: number;
  phase0: number;
  start: number;
  offset: number;
  scale: number;
}

/**
 * The walkable strip, and how much of it a walking body needs.
 *
 * The Prom's two building lines stand at |z| = PROM.halfDepth; their plinths
 * are 35 cm proud of that (|z| 8.65, `facadeMass`), the kerb strip runs
 * |z| 8.4–9.0, and the kerb furniture sits just inboard of it at |z| 7.0–7.9.
 * So the strip a pedestrian can cross without standing in stone is the
 * roadway out to the kerb — 8.4 less a shoulder.
 *
 * THIS WAS 5.8, AND THAT WAS THE COMPOSITION BUG. Pinning the ceiling
 * INSIDE the furniture band meant the lanes had to be pulled to |z| 3.4–5.2
 * to fit under it, which is the middle of the roadway — and the day-street
 * lens stands at z = 2, on the roadway, so the near lane passed within
 * 1.4 m of it. The judges photographed the result: a walker 2.5 m from the
 * plate filling 500+ px of frame height with its feet cropped off by the
 * letterbox. The furniture does not need a ceiling under it; `steer()` was
 * written to walk around exactly those volumes, and this is the ceiling it
 * was sized against.
 */
const WALK_MAX_Z = 7.9;
/** Half the stance, plus a shoulder. Across the walking direction. */
const WALK_HALF_W = 0.45;
/**
 * Half the STRIDE, along the walking direction — WAVE 14 REGRESSION FIX.
 *
 * `steer` used `WALK_HALF_W` for both axes of the clearance ellipse, and a
 * walker is not round: it walks along x with its legs swinging ±0.45 m fore
 * and aft of the hip, plus a boot. At the tangent point of the old ellipse
 * the along-track clearance collapses to zero, so a walker passing a crate
 * stack cleared it with its BODY and put its leading boot 20 cm inside the
 * crate face. The judges photographed it at day-street x395–412, y648–662.
 * Separating the two radii is the whole fix: 0.78 m clears a full stride and
 * costs the crowd about a quarter of a metre of pavement.
 */
const WALK_HALF_L = 0.78;
/** Both ends of the run stay on the paving slab and inside the row. */
const WALK_MIN_X = PROM.minX + 6;
const WALK_SPAN = PROM.maxX - PROM.minX - 12;
/** Obstacle bucket width for the steering lookup, metres. */
const WALK_BUCKET = 8;

class CrowdLayer {
  readonly group = new THREE.Group();
  private batch: CrowdBatch;
  private pools: THREE.InstancedMesh;
  private walkers: Walker[] = [];
  private elapsed = 0;
  /** Street furniture the walkers must go round, bucketed by x. */
  private blocked = new Map<number, PromProp[]>();
  // Scratch: this runs 96 times a frame and must not allocate.
  private mRoot = new THREE.Matrix4();
  private mLocal = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  private p = new THREE.Vector3();
  private s = new THREE.Vector3();
  private tint = new THREE.Color();
  /** Build width per look; it rides the instance matrix, not the skeleton. */
  private widths: number[] = [];

  constructor(t: Tracker) {
    const counts = CROWD_LOOKS.map(
      (_, l) => Math.floor((CROWD_N - 1 - l) / CROWD_LOOKS.length) + 1,
    );
    // THE CROWD IS THE PLAYER'S OWN FIGURE. Wave 14 baked a second, cheaper
    // humanoid out of rigid nodes because ninety-six skeletons was not a
    // conversation worth having; render/character.ts's crowd batch bakes the
    // walk cycle into a bone texture instead, so a look is ONE draw call of
    // the same authored mesh, skinned by the same curves, at LOD2.
    const looks: CrowdLook[] = CROWD_LOOKS.map((look, l) => ({
      appearance: sanitizeAppearance(look),
      count: counts[l] ?? 0,
    }));
    this.widths = looks.map((l) => buildWidth(l.appearance));
    this.batch = crowdBatch(looks, 2);
    this.group.add(this.batch.group);

    // The street's own plan, bucketed by x so a walker only ever tests the
    // handful of volumes it is actually near.
    for (const prop of promProps()) {
      if (prop.rx <= 0) continue;
      const reach = prop.rx + WALK_HALF_L;
      const lo = Math.floor((prop.x - reach) / WALK_BUCKET);
      const hi = Math.floor((prop.x + reach) / WALK_BUCKET);
      for (let b = lo; b <= hi; b++) {
        const bucket = this.blocked.get(b);
        if (bucket) bucket.push(prop);
        else this.blocked.set(b, [prop]);
      }
    }

    const slots = CROWD_LOOKS.map(() => 0);
    for (let i = 0; i < CROWD_N; i++) {
      const look = i % CROWD_LOOKS.length;
      const slot = slots[look] ?? 0;
      slots[look] = slot + 1;
      const lane = i % 2 === 0 ? 1 : -1;
      this.walkers.push({
        look,
        slot,
        lane,
        speed: 1.05 + rand2(KIT_SEED + 63, i, 2) * 0.5,
        phase0: rand2(KIT_SEED + 67, i, 6) * Math.PI * 2,
        start: rand2(KIT_SEED + 64, i, 3) * WALK_SPAN,
        // THE CROWD WALKS THE PAVEMENT, NOT THE CENTRE OF THE ROAD.
        //
        // Two lanes hugging the kerbs, with the roadway between them left
        // clear — which is both what a promenade looks like and what the
        // city references do. The band is set by what it has to clear at
        // each end: the kerb furniture starts at |z| 7.0, and the day-street
        // lens stands at z = 2, so 6.2 holds the NEAR lane 4.2 m off it at
        // worst — a passer-by at a third of frame height with both boots on
        // the paving, against the 500-px mannequin with cropped feet the
        // wave-13 judges shot at 1.4 m.
        offset: lane * (6.2 + rand2(KIT_SEED + 65, i, 4) * 1.2),
        // Height is free variation: the rig is uniform-scaled, so a 4%
        // spread reads as a crowd of different people, not of one clone.
        scale: 0.94 + rand2(KIT_SEED + 66, i, 5) * 0.13,
      });
    }

    // Contact: the sun casts a real shadow away from every walker, but the
    // wedge directly under the soles is what welds a figure to paving.
    this.pools = contactPools(CROWD_N, 0.5, 0.38);
    t.mat(this.pools.material as THREE.Material);
    this.group.add(this.pools);
  }

  /**
   * Walk round what is standing there.
   *
   * One pass over the few volumes within reach, pushing the walker just
   * clear of each. The side is chosen against the walker's OWN LANE rather
   * than its current z, so the choice is fixed for the whole approach and
   * nobody sidesteps twice; and a push that would leave the walkable strip
   * takes the other side instead, so steering can never resolve a kiosk by
   * walking into a shopfront.
   */
  private steer(x: number, lane: number): number {
    let z = lane;
    const b = Math.floor(x / WALK_BUCKET);
    for (const prop of this.blocked.get(b) ?? []) {
      // Along-track clearance uses the STRIDE radius, across-track the
      // shoulder radius: an ellipse sized for a body puts a boot in a crate.
      const reachX = prop.rx + WALK_HALF_L;
      const dx = x - prop.x;
      if (Math.abs(dx) >= reachX) continue;
      const span = (prop.rz + WALK_HALF_W) * Math.sqrt(1 - (dx / reachX) ** 2);
      if (Math.abs(z - prop.z) >= span) continue;
      const near = prop.z + (lane >= prop.z ? span : -span);
      const far = prop.z + (lane >= prop.z ? -span : span);
      z = Math.abs(near) <= WALK_MAX_Z ? near : far;
    }
    return Math.min(WALK_MAX_Z, Math.max(-WALK_MAX_Z, z));
  }

  tick(dt: number): void {
    this.elapsed += dt;
    this.walkers.forEach((w, wi) => {
      const x =
        WALK_MIN_X +
        ((((w.start + this.elapsed * w.speed * w.lane) % WALK_SPAN) + WALK_SPAN) % WALK_SPAN);
      const z = this.steer(x, w.offset);
      // The Prom's paving slab stands 4 cm proud of the terrain; a walker
      // whose soles are on the terrain is a walker sunk into the street.
      const ground = cityHeightAt(x, z) + 0.04;
      // The figure's own walk cycle: phase rides distance travelled, so a
      // fast walker's legs turn over faster without a second constant.
      const phase = w.phase0 + this.elapsed * w.speed * 3.6;
      const bob = Math.abs(Math.sin(phase * 2)) * 0.03;
      this.e.set(0, w.lane > 0 ? Math.PI / 2 : -Math.PI / 2, 0);
      this.q.setFromEuler(this.e);
      this.p.set(x, ground + bob * w.scale, z);
      const width = this.widths[w.look] ?? 1;
      this.s.set(w.scale * width, w.scale, w.scale * width);
      this.mRoot.compose(this.p, this.q, this.s);
      // A little value jitter on top of the four palettes — the light on a
      // street is never the same twice, and a crowd of exact repeats reads as
      // one sprite stamped in a row.
      const k = 0.86 + rand2(KIT_SEED + 68, wi, 7) * 0.3;
      this.tint.setRGB(k, k * 0.995, k * 0.985);
      this.batch.set(w.look, w.slot, this.mRoot, phase, this.tint);

      this.e.set(-Math.PI / 2, 0, 0);
      this.q.setFromEuler(this.e);
      this.p.set(x, ground + 0.012, z);
      this.s.set(0.78 * w.scale, 0.78 * w.scale, 1);
      this.mLocal.compose(this.p, this.q, this.s);
      this.pools.setMatrixAt(wi, this.mLocal);
    });
    this.batch.commit();
    this.pools.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.batch.dispose();
    this.pools.dispose();
  }
}

class AliveLayer {
  readonly group = new THREE.Group();
  private steam: THREE.Sprite[] = [];
  private steamBaseY: number[] = [];
  private semaphores: { group: THREE.Group; materials: THREE.MeshStandardMaterial[] }[] = [];
  private elapsed = 0;

  constructor(t: Tracker) {
    const steamTex = radialTexture(t, "rgba(240,240,236,0.55)", "rgba(240,240,236,0)");
    // Roofline vents on the rows, plus two street-level grates on the
    // Prom itself so the eye-level shot carries visible steam.
    const spots: Array<[number, number, number]> = [
      [-150, -34, 15],
      [-35, 28, 16],
      [90, -30, 14],
      [155, 30, 12],
      [240, -34, 10],
      [-110, -34, 17],
      [-100, 6.5, 1],
      [60, -6.5, 1],
    ];
    for (const [x, z, y] of spots) {
      const sprite = new THREE.Sprite(
        t.mat(
          new THREE.SpriteMaterial({
            map: steamTex,
            transparent: true,
            depthWrite: false,
            opacity: 0.5,
          }),
        ),
      );
      sprite.position.set(x, y, z);
      sprite.scale.set(6, 8, 1);
      this.group.add(sprite);
      this.steam.push(sprite);
      this.steamBaseY.push(y);
    }

    // Semaphore masts: brass poles whose signal arms flick between two
    // colors every few seconds — the aether-telegraph, visibly working.
    // On the street edge, in the gaps between buildings — round one had
    // them buried inside the row footprints.
    for (const [x, z] of [
      [-170, -8.4],
      [-52, 8.4],
      [96, -8.4],
      [190, 8.4],
    ] as const) {
      addCityAsset(t, this.group, "city-semaphore-mast", [{ x, y: 0, z }]);
      const arm = cloneAuthoredAsset("city-semaphore-arm");
      const materials: THREE.MeshStandardMaterial[] = [];
      arm.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        for (const mat of Array.isArray(object.material) ? object.material : [object.material]) {
          if (mat instanceof THREE.MeshStandardMaterial && mat.name.includes("amber"))
            materials.push(mat);
        }
      });
      arm.position.set(x, 10.6, z);
      this.group.add(arm);
      t.cleanup(() => disposeAuthoredObject(arm));
      this.semaphores.push({ group: arm, materials });
    }

    addCityAsset(t, this.group, "freight-dray", [
      { x: -64, y: cityHeightAt(-64, 0.6), z: 0.6, yaw: Math.PI },
      { x: 118, y: cityHeightAt(118, -1.2), z: -1.2, yaw: 0 },
      { x: 232, y: cityHeightAt(232, 1.4), z: 1.4, yaw: Math.PI },
    ]);
  }

  tick(dt: number): void {
    this.elapsed += dt;
    this.steam.forEach((sprite, i) => {
      const phase = (this.elapsed * 0.35 + i * 0.37) % 1;
      sprite.scale.set(4 + phase * 8, 5 + phase * 12, 1);
      sprite.position.y = (this.steamBaseY[i] ?? 12) + 2 + phase * 9;
      sprite.material.opacity = 0.42 * (1 - phase);
    });
    this.semaphores.forEach((arm, i) => {
      const beat = Math.floor(this.elapsed / 2.4 + i * 0.7) % 2 === 0;
      arm.group.rotation.z = beat ? 0.5 : -0.35;
      for (const material of arm.materials) {
        material.emissive.setHex(beat ? 0xd8b04a : 0x2fb3c0);
        material.color.setHex(beat ? 0xd8b04a : 0x2fb3c0);
      }
    });
  }

  dispose(): void {
    this.semaphores = [];
  }
}

// --- the scene ---------------------------------------------------------------

export function createCityScene(plan: AreaPlan): AreaScene {
  const t = new Tracker();
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(DAY_FOG);
  scene.fog = new THREE.Fog(DAY_FOG, FOG_NEAR, FOG_FAR);

  const worldRoot = new THREE.Group();
  scene.add(worldRoot);
  const origin = { x: 0, z: 0 };

  // MERIDIAN'S GROUND, AT LAST A MATERIAL (ground-plane wave).
  //
  // This layer was built with `detail` undefined, which meant the entire
  // ground stack — tiling sheet, normal, macro mask, the lot — compiled out
  // and the city floor rendered as a vertex colour times a hash jitter. Three
  // city verdicts have logged the consequence in the same words ("a flat,
  // untextured tan ground plane"), and no amount of prop dressing fixes a
  // plane that carries no surface. It gets a paving sheet of its own (cast
  // slabs on a running bond, joints packed with grit, polished where traffic
  // crosses and pitted where it does not) and the same macro tint mask every
  // other world already had. `repeat` is what sets the sheet's real size: the
  // terrain's uv is world metres / DETAIL_METERS, so 2.4 puts a slab course
  // at 2.5 m of ground.
  const cityPaving = pavingDetail(t);
  for (const map of [cityPaving.map, cityPaving.normalMap]) map.repeat.set(2.4, 2.4);
  const terrain = new TerrainLayer(
    undefined,
    cityPaving,
    {
      detailAmp: 0.92,
      // Paved, so no wind ripple: a drift bank across a city street is a
      // frontier read, and Meridian is not the frontier.
      ripple: 0,
      heightAt: cityHeightAt,
      half: plan.halfWidth,
      colorFor: (wx, wz, h, out) => {
        // r3: the inter-tower floor drops ~30%. The r2 skyline verdict logged
        // it twice — "the inter-tower floor is a fully-lit bright tan plane at
        // Y120 in direct violation of 'no ground plane in wides, canyon floors
        // unlit'", and "only 2 of the required ≥3 depth bands read" because a
        // bright mid-band floor flattened the staircase. The carpet blocks
        // hide most of it; where it shows through, it now reads as a canyon
        // bottom rather than a lit plain the towers are standing on.
        const ground = cityGroundAt(wx, wz);
        if (ground === "crown") out.setHex(0x62604f);
        else if (ground === "terraces") out.setHex(0x5f5b4d);
        else out.setHex(0x424a38);
        // Terrace benches lighten as they climb toward the crown.
        const lift = Math.min(1, Math.max(0, (h + 50) / 60));
        out.lerp(new THREE.Color(0x7b7668), lift * 0.5);
        const jitter = ((((wx * 13 + wz * 7) % 17) + 17) % 17) / 17;
        out.multiplyScalar(0.95 + jitter * 0.1);
      },
    },
    { macro: groundMacro(t) },
  );
  worldRoot.add(terrain.group);

  const facades = facadeKit(t);
  const updateCityLod = buildKit(t, worldRoot, facades);
  dressProm(t, worldRoot, plan, facades);
  dressPort(t, worldRoot);

  // The market wall: the same board the settlement hangs its bill on,
  // standing on its own Meridian footprint. One bill, many walls — the
  // scene travels, and so does its advertising.
  const posterWall = new PosterWallLayer(CITY_POSTER_WALL);
  worldRoot.add(posterWall.group);

  const traffic = new TrafficLayer(t);
  worldRoot.add(traffic.group);
  const alive = new AliveLayer(t);
  worldRoot.add(alive.group);
  const crowd = new CrowdLayer(t);
  worldRoot.add(crowd.group);

  const sky = buildDaySky(t);
  scene.add(sky);

  // The day rig, with authority: a warm sun well above the hemisphere's
  // fill (lit:shadow ~2.5x — the wave-9 correction), chromatic sky-blue
  // fill from above, warm bounce from the paving below.
  //
  // WAVE 10 r4 — REGRESSION FIX, +21%. r3's lifted-toe grade finally put
  // the day city inside the "no content under Y32 or over Y240" law, and
  // took the mid-tones down with it: "spaceport YAVG 141.2 → 117.5, and
  // mean colour moved from #8F8D85 (near-identical to the reference's
  // #8F8C8F noon-bone anchor) to #76756E… day-street 123.0 → 105.9, now 2
  // luma BELOW the 108–156 band". The critic's own read is the fix: "the
  // shape is right, the level is 20 luma low" — so this is a gain, not a
  // re-grade. The sky dome renders untone-mapped and is unaffected by
  // definition, so the "one bright atmospheric core" law (brightest 5% is
  // sky) survives the bump; only lit albedo moves.
  const sun = new THREE.DirectionalLight(0xffeed2, 3.75);
  sun.position.copy(SUN_DIR).multiplyScalar(100);
  scene.add(sun);
  scene.add(sun.target);
  /**
   * WAVE 14 — THE FILL WAS THE RIGHT BRIGHTNESS AND THE WRONG COLOUR.
   *
   * §e's law is that no shaded surface is ever achromatic, and the way it
   * is measured is the RATIO between a surface's shade chromaticity and its
   * own sunlit chromaticity: a shadow lit by sky must be cooler than the
   * same material lit by sun, or the frame has one light source in it
   * wearing two intensities. Measured on the delivered day-street plate,
   * paving, B/R: sunlit 0.780, cast shadow 0.786. Those are the same
   * number. The scene HAS a cool hemisphere and a warm ground bounce; what
   * it also had was 0.44 of near-neutral warm AMBIENT (#BFB9AC) poured over
   * everything, and ambient is by construction the one term that cannot
   * tell a shaded surface from a lit one. It was drowning both.
   *
   * So the ambient goes cool slate — it is standing in for sky, which is
   * what fills a street shadow — and the hemisphere's ground half goes
   * warmer and more saturated, because that is the term that survives on
   * the vertical shade faces where sky cannot reach. Same total energy,
   * two ends of the spectrum instead of one lump in the middle.
   */
  scene.add(new THREE.HemisphereLight(0xa8b6cc, 0xa8845c, 1.5));
  scene.add(new THREE.AmbientLight(0x93a6c2, 0.42));
  // THE CROWN SKYLINE HAD NOTHING TO REFLECT. Two thousand instanced towers
  // wearing a facade sheet whose glazing band is a quarter of its texels, a
  // spaceport full of steel, and no `scene.environment` in the scene at all —
  // so every conductor and every pane in three day vistas was returning the
  // punctual sun and nothing else, which is a flat shape with one hot spot.
  // The probe's sky is the city's own bone-noon fog and its sun is the same
  // vector the key uses, so the reflection agrees with the frame it is in.
  // The intensity is deliberately a fifth of the frontier's: this scene
  // already carries an ambient and a hemisphere, and what makes its metals
  // read is the response set's per-class specular gain, not more fill.
  const portProbe = installProbe(scene, t, {
    horizon: DAY_FOG,
    zenith: 0x8fb4d4,
    ground: 0x9a927f,
    sunDir: SUN_DIR,
    sunColor: 0xffeed2,
    // THE TWO HALVES ARE SET SEPARATELY, and the first cut is why. At
    // intensity 0.12 the probe's SKY put +12 luma on day-street, +7 on the
    // skyline and +8 on the spaceport — a probe's irradiance lands on every
    // up-facing dielectric in the frame, and this city's grade is measured to
    // the luma point. So the intensity drops to a fifth and the disc's
    // irradiance goes up by the same factor: 0.05 x 34 is the same specular
    // the metals were getting, and less than half the fill.
    sunIrradiance: 34,
    intensity: 0.05,
  });
  bindPortKitEnvironment(worldRoot, portProbe, 0.4);
  bindAuthoredEnvironment(worldRoot, portProbe, 0.4);

  /**
   * Sun shadows in the city — WAVE 10 r4, and the omission was total: the
   * city sun was built without `castShadow` at all, in a scene whose
   * meshes have been setting `castShadow = true` since r2. Every prop in
   * three day vistas was therefore lit but ungrounded, which is exactly
   * what the r3 spaceport verdict measured: "roughly 40% of the frame is
   * a flat, untextured tan ground plane with NO contact shadows or AO
   * under any crate, so the entire foreground prop field floats".
   *
   * Same near-field-cascade discipline as the frontier's rig (see
   * atmosphere.ts SHADOW_HALF_M): a ±115 m box that LEADS the lens, texel-
   * snapped so contact lines do not shimmer as it slides. The box follows
   * the RENDER camera rather than the player pose, via onBeforeRender on a
   * zero-cost anchor — the capture harness parks a freecam that the pose
   * knows nothing about, and a shadow box centred on an avatar three
   * hundred metres behind the lens is a shadow box centred on nothing.
   */
  const SHADOW_HALF = 115;
  const SHADOW_PX = 2048;
  const SHADOW_TEXEL = (SHADOW_HALF * 2) / SHADOW_PX;
  sun.castShadow = true;
  sun.shadow.mapSize.set(SHADOW_PX, SHADOW_PX);
  sun.shadow.camera.left = -SHADOW_HALF;
  sun.shadow.camera.right = SHADOW_HALF;
  sun.shadow.camera.top = SHADOW_HALF;
  sun.shadow.camera.bottom = -SHADOW_HALF;
  sun.shadow.camera.near = 10;
  sun.shadow.camera.far = 720;
  sun.shadow.bias = -0.0002;
  sun.shadow.normalBias = 0.06;
  /**
   * WAVE 14 — THE CITY NEVER GOT THE CONTACT-HARDENING FILTER.
   *
   * render/shadows.ts replaces three's fixed five-tap PCF kernel with a
   * PCSS one whose penumbra grows with caster distance, and it is installed
   * globally at import — so the patch has been live in Meridian since wave
   * 10. It could never do anything here, because the widening is expressed
   * as a multiple of `shadow.radius` and this rig never set one: three
   * leaves it at 1 TEXEL, which at 0.112 m/texel is a 11 cm ceiling on the
   * penumbra whatever the geometry does. Every shadow in three day vistas
   * was therefore the same hard-edged blob at every distance, which is
   * precisely §e's complaint. The frontier rig has run 9 texels since wave
   * 10; six here is the same 0.5–0.7 m of full-open penumbra against this
   * scene's coarser texel, so a doorway lintel hardens at its foot and
   * feathers across the pavement.
   */
  sun.shadow.radius = 6;
  // WAVE 14 — THE MISSING HALF OF THE RIG. Every round since r2 has enrolled
  // CASTERS; nothing ever enrolled the ground they cast onto, and the Prom
  // paving and port apron — the surfaces that ARE the bottom half of every
  // day plate — were built without `receiveShadow`. See `receivesShadows` in
  // render/common.ts for the bisection. One sweep at build, after the whole
  // set exists, so a prop added later cannot silently opt out again.
  receivesShadows(worldRoot);
  // `Scene.onBeforeRender` is the one hook that fires with the live camera
  // BEFORE the shadow pass (three r185: renderer.render line 17644 vs the
  // shadow map at 17702), so the box placed here is the box this frame's
  // shadows are actually rendered from — no one-frame lag, no dummy mesh.
  const camFwd = new THREE.Vector3();
  scene.onBeforeRender = (_r, _s, cam) => {
    updateCityLod(cam.position.x + origin.x, cam.position.z + origin.z);
    cam.getWorldDirection(camFwd);
    const fx = cam.position.x + camFwd.x * 62;
    const fz = cam.position.z + camFwd.z * 62;
    const sx = Math.round(fx / SHADOW_TEXEL) * SHADOW_TEXEL;
    const sz = Math.round(fz / SHADOW_TEXEL) * SHADOW_TEXEL;
    sun.target.position.set(sx, 0, sz);
    sun.target.updateMatrixWorld();
    sun.position.set(sx + SUN_DIR.x * 300, SUN_DIR.y * 300, sz + SUN_DIR.z * 300);
    sun.updateMatrixWorld();
  };

  return {
    scene,
    worldAnchor: worldRoot,
    cameraFar: 2600,
    // The day city's authored operator (wave 10 r2): ACES with a lifted
    // toe, so the merge's crushed shadow floor comes back without giving
    // up the contrast the merge bought. See CITY_BLACK_LIFT.
    toneMapping: THREE.CustomToneMapping,
    origin: () => origin,
    groundY: cityHeightAt,
    streamingPending: () => terrain.pending,
    tick(dt: number, _level?: number, pose?: PlayerPose) {
      traffic.sync(dt);
      alive.tick(dt);
      crowd.tick(dt);
      if (!pose) return;
      if (Math.abs(pose.x - origin.x) > REBASE_DIST || Math.abs(pose.z - origin.z) > REBASE_DIST) {
        origin.x = Math.round(pose.x / REBASE_GRID) * REBASE_GRID;
        origin.z = Math.round(pose.z / REBASE_GRID) * REBASE_GRID;
        worldRoot.position.set(-origin.x, 0, -origin.z);
      }
      terrain.update(pose.x, pose.z);
      sky.position.set(pose.x - origin.x, 0, pose.z - origin.z);
    },
    setLive() {
      // No booth on the Prom.
    },
    setNights(nights) {
      posterWall.setNights(nights);
    },
    dispose() {
      sun.shadow.dispose();
      terrain.dispose();
      traffic.dispose();
      alive.dispose();
      crowd.dispose();
      posterWall.dispose();
      t.dispose();
    },
  };
}
